import { create } from 'zustand';
import { CardData, IProjectMetrics, Sheet, Viewport, Department, OrgRole, Organization, KanbanTask, KanbanTaskStatus, Connection, Action, ActionPlan, ActionStatus, ActionOriginSource, ActionPriority, PostIt, MapType, Project } from '../types';
import { calculateMetrics } from '../utils/calculations';
import { recalculateFlow, generateId, calculatePostItAutoLayout } from '../utils/vsmGeometry';
import { calculateProjectMetrics, DashboardMetrics } from '../utils/leanMetrics';
import { createOrganizationFromPreset } from '../data/defaultOrganization';
import { isValidUuid } from '../utils/uuid';

export type ProjectStage = 'diagnosis' | 'kaizen' | 'planning' | 'execution' | 'sustainment';

// Tipo para grupos expandidos: subProcessId -> cards filhos
export type ExpandedGroupsMap = Map<string, CardData[]>;

export interface OptimizationSession {
  id: string;
  createdAt: string;
  baseMode: 'current';
  baseSheetId: string | null;
}

export type OptimizationLayerItemType = 'postit' | 'kaizen' | 'note';

export interface OptimizationLayerItem {
  id: string;
  sessionId: string;
  type: OptimizationLayerItemType;
  linkedNodeId: string | null;
  payload: PostIt;
  createdAt: string;
  updatedAt: string;
}

const getOptimizationLayerType = (postIt: PostIt): OptimizationLayerItemType => {
  if (postIt.type === 'action') return 'kaizen';
  if (postIt.type === 'data') return 'note';
  return 'postit';
};

const syncOptimizationLayerPostIts = (
  layer: OptimizationLayerItem[],
  previousPostIts: PostIt[],
  nextPostIts: PostIt[],
  sessionId: string,
): OptimizationLayerItem[] => {
  const previousIds = new Set(previousPostIts.map((postIt) => postIt.id));
  const nextIds = new Set(nextPostIts.map((postIt) => postIt.id));
  const layeredIds = new Set(layer.map((item) => item.payload.id));
  const now = new Date().toISOString();

  const layerByPostItId = new Map(layer.map((item) => [item.payload.id, item]));
  const touchedPostIts = nextPostIts.filter((postIt) => !previousIds.has(postIt.id) || layeredIds.has(postIt.id));
  const touchedIds = new Set(touchedPostIts.map((postIt) => postIt.id));

  const preserved = layer.filter((item) => nextIds.has(item.payload.id) && !touchedIds.has(item.payload.id));
  const upserted = touchedPostIts.map((postIt) => {
    const existing = layerByPostItId.get(postIt.id);
    return {
      id: existing?.id ?? `opt-${postIt.id}`,
      sessionId,
      type: getOptimizationLayerType(postIt),
      linkedNodeId: postIt.fixedToNodeId ?? null,
      payload: {
        ...postIt,
        impactos: postIt.impactos ? postIt.impactos.map((impact) => ({ ...impact })) : undefined,
        relativeOffset: postIt.relativeOffset ? { ...postIt.relativeOffset } : { x: 0, y: 0 },
      },
      createdAt: existing?.createdAt ?? now,
      updatedAt: now,
    };
  });

  return [...preserved, ...upserted];
};

const dedupeCardsById = (cards: CardData[]): CardData[] => {
  const map = new Map<string, CardData>();
  cards.forEach(card => {
    map.set(card.id, card);
  });
  return Array.from(map.values());
};

interface ProjectStore {
  projects: Project[];
  currentProjectId: string | null;
  setCurrentProjectId: (id: string | null) => void;
  /** Mapa novo ainda sem registro de projeto. Impede reaproveitar o fluxo anterior ao salvar. */
  isNewMapDraft: boolean;
  beginNewMapDraft: () => void;
  currentBlueprintId: string | null;
  setCurrentBlueprintId: (id: string | null) => void;
  addProject: (project: Project) => void;
  // Estado
  sheets: Sheet[];
  activeSheetId: string | null;
  metrics: IProjectMetrics;
  selectedCardId: string | null;
  simulatedDemand: number | null;
  simulatedHeadcount: number | null;
  simulationBaselineData: Record<string, { p: number; tt: number; lt: number }> | null;
  organization: Organization;
  postItsViewMode: 'hidden' | 'compact' | 'detailed';
  previousPostItsViewMode: 'compact' | 'detailed'; // Para restaurar após ocultar
  isKanbanOpen: boolean;
  isKaizenMode: boolean;
  hasFutureMap: boolean; // Flag para indicar se o mapa futuro foi gerado
  /** Assinatura da base usada na última geração do mapa futuro. */
  futureMapSourceKey: string | null;
  /** Assinatura do estado atual já incorporada na otimização. */
  kaizenBaselineKey: string | null;
  futureProjectionKey: () => string;
  refreshFutureStateMap: () => boolean;
  stage: ProjectStage;
  completedStages: ProjectStage[];
  journeyDeviationAlert: boolean;
  viewMode: 'current_clean' | 'kaizen_work' | 'future_simulated'; // Modo de visualização: limpo, trabalho, ou futuro simulado
  globalDemand: number;
  workTime: number;
  taktTime: number;
  // V104.0 — Configuração Global de Fábrica (Capacidade)
  factoryShifts: number;
  factoryMinPerShift: number;
  factoryWorkDays: number;
  setFactoryShifts: (v: number) => void;
  setFactoryMinPerShift: (v: number) => void;
  setFactoryWorkDays: (v: number) => void;
  dashboardMetrics: DashboardMetrics;
  isWidgetOpen: boolean;
  isFullDashboardOpen: boolean;
  saveStatus: 'idle' | 'saving' | 'saved' | 'error';
  currentMapDisplayName: string | null;
  setCurrentMapDisplayName: (name: string | null) => void;
  heatmapMode: boolean;
  setHeatmapMode: (active: boolean) => void;
  colorCardsBySector: boolean;
  setColorCardsBySector: (active: boolean) => void;
  postIts: PostIt[];
  optimizationSession: OptimizationSession | null;
  optimizationLayer: OptimizationLayerItem[];
  mapType: MapType;
  _calcTimer: NodeJS.Timeout | null; // Timer interno para debounce de cálculos
  
  // x} GRUPOS EXPANDIDOS - Para cálculo de edges com Flattened View
  expandedGroups: ExpandedGroupsMap;
  setExpandedGroups: (groups: ExpandedGroupsMap) => void;
  expandGroup: (subProcessId: string, childCards: CardData[]) => void;
  collapseGroup: (subProcessId: string) => void;
  clearExpandedGroups: () => void;

  // V28.0 — Estilo visual dos ícones VSM
  // V221: adicionado 'technical' — linhas pretas retas e tipografia sans-serif limpa (engenharia)
  vsmDesignStyle: 'blueprint' | 'sketch' | 'technical';
  setVsmDesignStyle: (style: 'blueprint' | 'sketch' | 'technical') => void;
  vsmCardColor: string;
  setVsmCardColor: (color: string) => void;

  // Actions - Map Type
  setMapType: (type: MapType) => void;

  // Actions - Cards (trabalham na sheet ativa)
  setCards: (cards: CardData[]) => void;
  addCard: (card: CardData) => void; // Nova função atômica para adicionar card
  setPostIts: (postIts: PostIt[] | ((prev: PostIt[]) => PostIt[])) => void;
  ensureOptimizationSession: () => void;
  setOptimizationLayer: (items: OptimizationLayerItem[]) => void;
  addOptimizationLayerItem: (item: Omit<OptimizationLayerItem, 'id' | 'sessionId' | 'createdAt' | 'updatedAt'>) => void;
  updateCardData: (id: string, data: Partial<CardData>) => void;
  updatePostIt: (id: string, data: Partial<PostIt>) => void;
  updateMultipleCardsData: (ids: string[], data: Partial<CardData>) => void;
  recalculateFlowVolumes: () => void;
  setSelectedCardId: (id: string | null) => void;
  setSimulatedDemand: (demand: number | null) => void;
  setSimulatedHeadcount: (headcount: number | null) => void;
  createSimulationBaseline: () => void;
  resetToBaseline: () => void;
  setSaveStatus: (status: 'idle' | 'saving' | 'saved' | 'error') => void;
  
  // Actions - Sheets
  setSheets: (sheets: Sheet[], activeSheetId?: string) => void;
  addSheet: (name: string) => string; // Retorna o ID da nova sheet
  setActiveSheet: (id: string) => void;
  duplicateSheet: (id: string) => string; // Retorna o ID da sheet duplicada
  renameSheet: (id: string, newName: string) => void;
  deleteSheet: (id: string) => void;
  updateSheetViewport: (id: string, viewport: Viewport) => void;
  updateSheetColor: (id: string, color: string) => void;
  
  // Sugestões de setores criadas nos cards (temporárias, não cadastradas no Admin)
  sectorSuggestions: string[];
  addSectorSuggestion: (name: string) => void;
  removeSectorSuggestion: (name: string) => void;
  promoteSectorSuggestion: (name: string) => string; // Promove para departamento oficial, retorna ID

  // Actions - Organization
  setOrganization: (organization: Organization) => void;
  addDepartment: (department: Omit<Department, 'id'>) => string; // Retorna o ID
  updateDepartment: (id: string, data: Partial<Department>) => void;
  deleteDepartment: (id: string) => void;
  addRole: (role: Omit<OrgRole, 'id'>) => string; // Retorna o ID
  updateRole: (id: string, data: Partial<OrgRole>) => void;
  deleteRole: (id: string) => void;
  getRoleById: (id: string) => OrgRole | undefined;
  getDepartmentById: (id: string) => Department | undefined;
  getRolesByDepartment: (departmentId: string) => OrgRole[];
  
  // Computed
  getActiveSheet: () => Sheet | undefined;
  getCards: () => CardData[]; // Retorna cards da sheet ativa (compatibilidade)
  getSelectedCard: () => CardData | undefined;
  getCardsWithSimulation: () => CardData[];
  
  // Actions - Post-its View Mode
  setPostItsViewMode: (mode: 'hidden' | 'compact' | 'detailed') => void;
  togglePostItsVisibility: () => void; // Toggle entre oculto/mostrar
  minimizePostIts: () => void; // Toggle entre compact (miniaturas) e detailed (normal)
  
  // Actions - Post-its (adicionar post-it a um card específico)
  addPostIt: (cardId: string, postIt: { id: string; type: string; text: string; x: number; y: number; fixedToNodeId: string | null; relativeOffset: { x: number; y: number }; color: string; isMinimized?: boolean }) => void;
  
  // Actions - Kanban
  toggleKanban: () => void;
  toggleKaizenMode: () => void;
  syncPostItToKanban: (postItId: string, postItType: string, postItText: string, postItColor?: string, linkedNodeId?: string | null) => void;
  removeKanbanTaskByPostItId: (postItId: string) => void;
  updateKanbanTaskLinkedPostIt: (taskId: string, newText: string, updatePostItCallback?: (postItId: string, text: string) => void) => void;
  addKanbanTask: (task: Omit<KanbanTask, 'id' | 'createdAt' | 'updatedAt'>) => string; // Retorna o ID da tarefa criada
  updateKanbanTask: (id: string, data: Partial<KanbanTask>, updatePostItCallback?: (postItId: string, text: string) => void) => void;
  moveKanbanTask: (id: string, newStatus: KanbanTaskStatus) => void;
  // Actions - Widget Lateral
  toggleWidget: () => void;
  expandDashboard: () => void; // Fecha widget e abre modal full
  // Actions - Dashboard Completo (Modal)
  toggleFullDashboard: () => void;
  recalculateDashboardMetrics: () => void;
  
  // x} ISOLAMENTO DE DADOS ENTRE MODOS (current / kaizen / future)
  // Cada modo mantém seu próprio snapshot de cards E postIts. Ações em um modo não afetam os outros.
  _modeSnapshots: {
    current: { cards: CardData[]; postIts: PostIt[] } | null;
    kaizen: { cards: CardData[]; postIts: PostIt[] } | null;
    future: { cards: CardData[]; postIts: PostIt[] } | null;
  };
  saveAndSwitchMode: (fromMode: 'current' | 'kaizen' | 'future', toMode: 'current' | 'kaizen' | 'future') => void;

  // Actions - Project Stages
  setStage: (stage: ProjectStage) => void;
  completeStage: (stage: ProjectStage) => void;
  clearJourneyDeviationAlert: () => void;
  setViewMode: (mode: 'current_clean' | 'kaizen_work' | 'future_simulated') => void;
  generateFutureStateMap: () => boolean; // Gera ou atualiza o mapa futuro e abre esse modo
  restoreOriginalState: () => void; // Restaura valores originais dos cards
  
  // Actions - Action Plans
  actionPlans: ActionPlan[];
  actions: Action[];
  addActionPlan: (plan: Omit<ActionPlan, 'id' | 'createdAt' | 'updatedAt' | 'actionIds'>) => string;
  updateActionPlan: (id: string, data: Partial<ActionPlan>) => void;
  deleteActionPlan: (id: string) => void;
  getActionPlan: (id: string) => ActionPlan | undefined;
  getActionPlans: () => ActionPlan[];
  
  // Actions - Actions
  addAction: (action: Omit<Action, 'id' | 'createdAt' | 'updatedAt'>) => string;
  addActionFromPostIt: (postItId: string, text: string, linkedNodeId?: string | null) => string; // x} NOVA ACTION
  updateAction: (id: string, data: Partial<Action>) => void;
  deleteAction: (id: string) => void;
  getAction: (id: string) => Action | undefined;
  getActions: () => Action[];
  setActions: (actions: Action[]) => void;

  mergeProjectActions: (projectId: string, actions: Action[]) => void;
  getActionsByPlan: (actionPlanId: string) => Action[];
  getActionsByNode: (linkedNodeId: string) => Action[];
  getActionsByProject: (projectId: string) => Action[];
  moveAction: (id: string, newStatus: ActionStatus) => void;
  
  // Reset para novo mapa
  resetNewProjectState: () => void;

  /**
   * V230 — Inicializa um projeto novo do zero, garantindo que TODOS os três
   * cenários (current/kaizen/future) recebam o mesmo chassi em branco.
   * Corrige o bug de contaminação cruzada quando o usuário cria "Novo Mapa"
   * a partir de um projeto existente: as abas ATUAL/OTIMIZAÇÃO/FUTURO
   * continuavam exibindo dados do mapa anterior porque os snapshots por
   * cenário não eram limpos junto com o canvas ativo.
   */
  initializeBlankProject: (params: {
    mapType: MapType;
    baseCards: CardData[];
    displayName?: string | null;
  }) => void;

  // Migração
  migrateLegacyState: (legacyCards: CardData[]) => void;
  migrateKanbanTasksToActions: () => void;
  
  // Simulação de Cenário Futuro (Kaizen)
  simulateFutureState: () => { 
    futureCards: CardData[]; 
    futureMetrics: IProjectMetrics;
    currentMetrics: IProjectMetrics;
    improvements: {
      leadTimeReduction: number;
      costReduction: number;
      efficiencyGain: number;
      annualSaving: number;
    };
  };
  
  getFutureMetrics: () => {
    current: IProjectMetrics;
    future: IProjectMetrics;
  };
}

// Função auxiliar para calcular carga
const calculateCarga = (card: CardData): number => {
  // Usar volume propagado (incomingVolume) ou demanda direta (entrada)
  const demand = Number(card.incomingVolume || card.entrada || 0); // Peças/dia que chegam neste card
  const tra = Number(card.tt || 0); // TRA (Tempo de Realização) em minutos
  const availableTime = 480; // Turno padrão de 8h em minutos

  // Cálculo da Carga (Load)
  // Fórmula: (Total Trabalho Necessário / Tempo Disponível) * 100
  let loadPercent = 0;
  if (availableTime > 0 && tra > 0 && demand > 0) {
    const totalWorkNeeded = demand * tra;
    loadPercent = (totalWorkNeeded / availableTime) * 100;
  }

  return loadPercent;
};

// Função auxiliar para recalcular carga de todos os cards após propagação de volume
const updateCardsWithCarga = (cards: CardData[]): CardData[] => {
  return cards.map(card => {
    const carga = calculateCarga(card);
    return {
      ...card,
      carga
    };
  });
};

const impactChangesTheCard = (impact: NonNullable<PostIt['impactos']>[number]) =>
  Boolean(impact.targetNodeId) && (impact.tipo === 'ELIMINAR' || Number(impact.reducao) > 0);

const postItsHaveQuantifiedImpact = (postIts?: PostIt[]) =>
  (postIts || []).some(postIt => (postIt.impactos || []).some(impactChangesTheCard));

const futureCardKey = (card: CardData) =>
  [
    card.id,
    card.tipo,
    card.atividade ?? '',
    card.status ?? '',
    Number(card.tt) || 0,
    Number(card.lt) || 0,
    Number(card.tp) || 0,
    (card.conexoes_saida || []).map(conn => conn.targetId).join(','),
  ].join(':');

const futurePostItKey = (postIt: PostIt) =>
  [
    postIt.id,
    postIt.type,
    postIt.text ?? '',
    postIt.fixedToNodeId ?? '',
    (postIt.impactos || [])
      .map(impact => `${impact.tipo}:${Number(impact.reducao) || 0}:${impact.targetNodeId ?? ''}`)
      .join(','),
  ].join(':');

const futureCardsKey = (cards: CardData[]) => cards.map(futureCardKey).join(';');

const futureInputKey = (cards: CardData[], postIts: PostIt[]) =>
  `${futureCardsKey(cards)}#${postIts.map(futurePostItKey).join(';')}`;

const isOptimizationOnlyCard = (card: CardData) => {
  const extra = card as CardData & { vsmOptimizationOnly?: boolean; vsmIcon?: string };
  return Boolean(extra.vsmOptimizationOnly) || card.tipo === 'kaizen' || extra.vsmIcon === 'kaizen';
};

const cloneSnapshotCards = (cards: CardData[]): CardData[] =>
  cards.map(card => ({
    ...card,
    conexoes_saida: card.conexoes_saida.map(conn => ({ ...conn })),
    resources: card.resources ? [...card.resources] : [],
    originalConexoes: card.originalConexoes
      ? card.originalConexoes.map(conn => ({ ...conn }))
      : undefined,
  }));

const cloneSnapshotPostIts = (postIts: PostIt[]): PostIt[] =>
  postIts.map(postIt => ({
    ...postIt,
    impactos: postIt.impactos ? postIt.impactos.map(impact => ({ ...impact })) : undefined,
    relativeOffset: postIt.relativeOffset ? { ...postIt.relativeOffset } : undefined,
  }));

type ModeSnapshot = { cards: CardData[]; postIts: PostIt[] };

const readWorkingSnapshots = (state: {
  viewMode: 'current_clean' | 'kaizen_work' | 'future_simulated';
  postIts: PostIt[];
  _modeSnapshots: { current: ModeSnapshot | null; kaizen: ModeSnapshot | null; future: ModeSnapshot | null };
  getActiveSheet: () => { cards: CardData[] } | null | undefined;
}) => {
  const snapshots = {
    current: state._modeSnapshots.current,
    kaizen: state._modeSnapshots.kaizen,
    future: state._modeSnapshots.future,
  };
  const active = state.getActiveSheet();
  const modeKey = state.viewMode === 'kaizen_work'
    ? 'kaizen'
    : state.viewMode === 'future_simulated'
      ? 'future'
      : 'current';
  if (active && modeKey !== 'future') {
    snapshots[modeKey] = { cards: active.cards, postIts: state.postIts };
  }
  return snapshots;
};

/** O futuro nasce do estado atual corrigido, com os post-its e impactos da otimização. */
const projectionInputs = (snapshots: { current: ModeSnapshot | null; kaizen: ModeSnapshot | null }) => {
  const currentCards = snapshots.current?.cards ?? [];
  const kaizenCards = snapshots.kaizen?.cards ?? [];
  const currentIds = new Set(currentCards.map(card => card.id));
  const extras = kaizenCards.filter(card => !currentIds.has(card.id) && isOptimizationOnlyCard(card));
  const cards = currentCards.length > 0 ? [...currentCards, ...extras] : kaizenCards;
  const kaizenPostIts = snapshots.kaizen?.postIts ?? [];
  const postIts = kaizenPostIts.length > 0 ? kaizenPostIts : (snapshots.current?.postIts ?? []);
  return { cards, postIts };
};

/** Projeta o mapa futuro: reduz TP/TRA e retira do fluxo as atividades eliminadas. */
const projectFutureOfficeCards = (cards: CardData[], postIts: PostIt[]): CardData[] => {
  const impacts = (postIts || []).flatMap(postIt => postIt.impactos || []);
  const withTimes = cards.map(card => {
    const mine = impacts.filter(impact => impact.targetNodeId === card.id);
    const originalTT = card.originalTT !== undefined ? card.originalTT : card.tt;
    const originalLT = card.originalLT !== undefined ? card.originalLT : card.lt;
    const originalStatus = card.originalStatus !== undefined ? card.originalStatus : card.status;
    const sourceConnections = (card.originalConexoes || card.conexoes_saida || []).map(conn => ({ ...conn }));

    if (!mine.some(impactChangesTheCard)) {
      return {
        ...card,
        status: originalStatus,
        tt: originalTT,
        lt: originalLT,
        conexoes_saida: sourceConnections,
        originalTT,
        originalLT,
        originalStatus,
        originalConexoes: sourceConnections.map(conn => ({ ...conn })),
      };
    }

    const eliminate = mine.some(impact => impact.tipo === 'ELIMINAR');
    let newTT = Number(originalTT || 0);
    let newLT = Number(originalLT || 0);
    let newStatus = originalStatus;

    if (eliminate) {
      newTT = 0;
      newLT = 0;
      newStatus = 'removido' as const;
    } else {
      mine.forEach(impact => {
        if (!impactChangesTheCard(impact)) return;
        const factor = (100 - Number(impact.reducao || 0)) / 100;
        if (impact.tipo === 'TRA') newTT = Math.max(0, newTT * factor);
        if (impact.tipo === 'TP') newLT = Math.max(0, newLT * factor);
      });
    }

    return {
      ...card,
      tt: newTT,
      lt: newLT,
      status: newStatus,
      conexoes_saida: sourceConnections,
      originalTT,
      originalLT,
      originalStatus,
      originalConexoes: sourceConnections.map(conn => ({ ...conn })),
      futureApplied: true,
    };
  });

  const removedCardIds = new Set(withTimes.filter(card => card.status === 'removido').map(card => card.id));
  if (removedCardIds.size === 0) return withTimes;

  const cardById = new Map(withTimes.map(card => [card.id, card]));
  const isBifurcationArrow = (source: CardData | undefined, conn: Connection) =>
    source?.tipo === 'decisao' && conn.sourceHandle === 'bottom';
  // Mesma regra do roteamento: source à direita do destino é seta de retorno (vermelha).
  const isReturnArrow = (source: CardData | undefined, target: CardData | undefined) =>
    Boolean(source && target) && (source!.id === target!.id || source!.posicao.x > target!.posicao.x - 10);

  const findAllNextValidCards = (cardId: string, visited: Set<string> = new Set()): string[] => {
    if (visited.has(cardId)) return [];
    visited.add(cardId);
    const card = cardById.get(cardId);
    if (!card) return [];
    if (card.status !== 'removido') return [cardId];
    const validNextCards: string[] = [];
    for (const conn of card.conexoes_saida || []) {
      // A seta de baixo da bifurcação é o desvio. Se a bifurcação sai, essa seta não segue.
      if (isBifurcationArrow(card, conn)) continue;
      // Retorno do caminho excluído não pode reaparecer no card que ficou.
      if (isReturnArrow(card, cardById.get(conn.targetId))) continue;
      validNextCards.push(...findAllNextValidCards(conn.targetId, new Set(visited)));
    }
    return validNextCards;
  };

  return withTimes.map(card => {
    if (card.status === 'removido') return card;
    const newConnections: Connection[] = [];
    (card.conexoes_saida || []).forEach(conn => {
      if (!removedCardIds.has(conn.targetId)) {
        newConnections.push(conn);
        return;
      }
      // Eliminar o desvio da bifurcação também elimina a seta desse desvio.
      if (isBifurcationArrow(card, conn)) return;
      const keptTargets = findAllNextValidCards(conn.targetId).filter(nextValidId =>
        !isReturnArrow(card, cardById.get(nextValidId)),
      );
      keptTargets.forEach((nextValidId, index) => {
        newConnections.push({
          ...conn,
          id: index === 0 ? conn.id : `${conn.id}_${index}`,
          targetId: nextValidId,
          sourceHandle: card.tipo === 'decisao' ? conn.sourceHandle : 'right',
        });
      });
    });
    const uniqueConnections = newConnections.filter((conn, index, self) =>
      index === self.findIndex(item => item.targetId === conn.targetId),
    );
    return { ...card, conexoes_saida: uniqueConnections };
  });
};

// Função auxiliar para criar organização padrão (vazia — preset acessível via "Resetar para Padrão" no OrgManager)
const createDefaultOrganization = (): Organization => {
  return { departments: [], roles: [] };
};

export const useProjectStore = create<ProjectStore>((set, get) => ({
  projects: [],
  
  currentProjectId: (() => {
    try {
      const id = localStorage.getItem('wf_current_project_id');
      if (id && !isValidUuid(id)) {
        localStorage.removeItem('wf_current_project_id');
        return null;
      }
      return id;
    } catch {
      return null;
    }
  })(),
  
  isNewMapDraft: false,

  setCurrentProjectId: (id) => {
    const validId = id && isValidUuid(id) ? id : null;
    try {
      if (validId) localStorage.setItem('wf_current_project_id', validId);
      else localStorage.removeItem('wf_current_project_id');
    } catch { /* noop */ }
    set((state) => ({
      currentProjectId: validId,
      isNewMapDraft: validId ? false : state.isNewMapDraft,
    }));
  },

  beginNewMapDraft: () => {
    try { localStorage.removeItem('wf_current_project_id'); } catch { /* noop */ }
    set({ isNewMapDraft: true, currentProjectId: null });
  },

  currentBlueprintId: null,
  setCurrentBlueprintId: (id) => set({ currentBlueprintId: id }),
  
  addProject: (project) => set((state) => ({ 
    projects: [...state.projects, project] 
  })),
  // Estado inicial
  sheets: [],
  activeSheetId: null,
  metrics: {
    totalLTSum: 0,
    totalTT: 0,
    flowEfficiency: '0',
    nvaPercentage: '0',
    totalActivities: 0,
    totalDecisions: 0,
    timeVA: 0,
    timeAVO: 0,
    timeNVA: 0,
    longestPath: '',
    longestPathProb: '0',
    longestPathIds: [],
    longestPathTP: '0',
    longestPathTRA: '0',
    longestPathP: '0',
    shortestPath: '',
    shortestPathProb: '0',
    shortestPathIds: [],
    shortestPathTP: '0',
    shortestPathTRA: '0',
    shortestPathP: '0',
    averageLeadTime: '0',
    realBottleneck: null,
    demand: 0,
    capacitySystem: 0,
    backlog: 0,
    wipTheoretical: '0',
    maxLoad: '0',
    monthlyOpCost: 0,
    hasLoop: false,
    vaPercentage: '0',
    taktTime: '0',
    cycleTime: '0',
    taktViolation: '0',
    waitTimeTotal: '0',
    waitTimePercent: '0',
    reworkRate: '0',
    slaTarget: '0',
    slaAchieved: false,
    heatMapData: null,
    fragmentationPercent: '0',
    activityRanking: [],
    totalPossiblePaths: 0,
    averageTimePerVisit: '0',
    pathComplexityPercent: '0',
    pathIntensityScore: '0',
    averagePathDepth: '0',
    rolledThroughputYield: '0',
    totalReworkTime: '0',
    hiddenFactoryCost: '0',
    flowEfficiencyPremium: '0',
    averageInterruptionScore: '0',
  },
  selectedCardId: null,
  simulatedDemand: null,
  simulatedHeadcount: null,
  simulationBaselineData: null,
  organization: createDefaultOrganization(),
  sectorSuggestions: [],
  postItsViewMode: 'detailed', // Default to detailed (modo normal)
  previousPostItsViewMode: 'detailed', // Modo padrão quando reaparecer
  isKanbanOpen: false,
  isKaizenMode: false, // Modo Kaizen desativado por padrão
  hasFutureMap: false,
  futureMapSourceKey: null,
  kaizenBaselineKey: null,
  _modeSnapshots: { current: null, kaizen: null, future: null }, // x} Snapshots de isolamento entre modos
  stage: 'diagnosis', // Etapa inicial: Diagnóstico
  completedStages: [], // Nenhuma etapa concluída inicialmente
  journeyDeviationAlert: false,
  viewMode: 'current_clean', // Visualização padrão: estado atual
  actionPlans: [],
  actions: [],
  globalDemand: 100, // Demanda padrão: 100 peças/dia
  workTime: 480, // Tempo disponível padrão: 480 min/dia (8 horas)
  taktTime: 4.8, // Takt time padrão: 480 / 100 = 4.8 min/pç
  // V104.0 — Configuração Global de Fábrica
  factoryShifts: 2,
  factoryMinPerShift: 480,
  factoryWorkDays: 20,
  setFactoryShifts: (v) => set({ factoryShifts: v }),
  setFactoryMinPerShift: (v) => set({ factoryMinPerShift: v }),
  setFactoryWorkDays: (v) => set({ factoryWorkDays: v }),
  dashboardMetrics: {
    totalCost: 0,
    totalFTE: 0,
    bottleneckId: null,
    bottleneckName: null,
    leadTime: 0,
    efficiency: 0,
  },
  isWidgetOpen: false,
  isFullDashboardOpen: false,
  saveStatus: 'idle',
  currentMapDisplayName: null,
  setCurrentMapDisplayName: (name) => set({ currentMapDisplayName: name }),
  heatmapMode: false,
  setHeatmapMode: (active) => set({ heatmapMode: active }),
  colorCardsBySector: false,
  setColorCardsBySector: (active) => set({ colorCardsBySector: active }),
  postIts: [],
  optimizationSession: null,
  optimizationLayer: [],
  mapType: (() => {
    try {
      const stored = localStorage.getItem('wf_current_map_type');
      return stored === 'FACTORY' || stored === 'OFFICE' ? stored : 'OFFICE';
    } catch {
      return 'OFFICE';
    }
  })(),
  vsmDesignStyle: 'sketch',
  vsmCardColor: '#7dd3fc',
  _calcTimer: null,
  
  // x} GRUPOS EXPANDIDOS - Estado inicial vazio
  expandedGroups: new Map(),
  setExpandedGroups: (groups) => set({ expandedGroups: groups }),
  expandGroup: (subProcessId, childCards) => set((state) => {
    const newMap = new Map(state.expandedGroups);
    newMap.set(subProcessId, childCards);
    return { expandedGroups: newMap };
  }),
  collapseGroup: (subProcessId) => set((state) => {
    const newMap = new Map(state.expandedGroups);
    newMap.delete(subProcessId);
    return { expandedGroups: newMap };
  }),
  clearExpandedGroups: () => set({ expandedGroups: new Map() }),

  // Actions - Map Type
  setMapType: (mapType: MapType) => {
    try { localStorage.setItem('wf_current_map_type', mapType); } catch { /* noop */ }
    set({ mapType });
  },
  setVsmDesignStyle: (vsmDesignStyle) => set({ vsmDesignStyle }),
  setVsmCardColor: (vsmCardColor) => set({ vsmCardColor }),

  // Actions - Cards (trabalham na sheet ativa)
  setCards: (cards: CardData[]) => {
    const normalizedCards = dedupeCardsById(cards);
    const { sheets, activeSheetId, actions: currentActions } = get();
    
    // Sincronizar com a lista de ações: encontrar cards do tipo 'acao' em todas as sheets (ou na lista fornecida)
    let updatedActions = [...currentActions];
    
    // Obter todos os cards de todas as sheets para garantir que não removeremos ações de outras sheets
    const allCards = sheets.length > 0 
      ? sheets.reduce((acc, sheet) => [...acc, ...sheet.cards], [] as CardData[])
      : cards; // Fallback se sheets estiver vazio
    
    // REMOVIDO: Sincronização de cards verdes (usuário solicitou apenas post-its)
    const actionCards: CardData[] = [];
    
    // 1. Identificar ações que devem ser removidas (cards que sumiram do sistema completo)
    updatedActions = updatedActions.filter(action => {
      if (action.origin === 'canvas' || action.linkedNodeId) {
        const linkedId = action.linkedNodeId || action.id;
        
        // Verificar se é um post-it
        const isPostIt = get().postIts.some(p => p.id === linkedId);
        if (isPostIt) return true; // Manter se for post-it (sincronizado em syncPostItToKanban)

        // Verificar se o card existe em QUALQUER lugar (nas sheets atuais ou nos novos cards)
        const existsInCurrentSheets = sheets.some(s => s.cards.some(c => c.id === linkedId));
        const existsInNewCards = normalizedCards.some(c => c.id === linkedId);
        return existsInCurrentSheets || existsInNewCards;
      }
      return true;
    });
    
    // 2. Adicionar ou atualizar ações baseadas nos cards atuais (REMOVIDO conforme pedido)
    // Apenas post-its devem entrar na lista de ações agora via syncPostItToKanban

    if (!activeSheetId) {
      // Se não houver sheet ativa, criar uma
      const newSheetId = generateId();
      
      const newSheet: Sheet = {
        id: newSheetId,
        name: 'MAPA 1',
        cards: normalizedCards // Atualizar IMEDIATAMENTE
      };
      
      // Atualizar estado IMEDIATAMENTE para UI responsiva
      set({ 
        sheets: [newSheet], 
        activeSheetId: newSheetId,
        actions: updatedActions
      });
      
      // Calcular métricas DEPOIS de forma assíncrona
      setTimeout(() => {
        const cardsWithVolumes = recalculateFlow(normalizedCards);
        const cardsWithCarga = updateCardsWithCarga(cardsWithVolumes);
        const metrics = calculateMetrics(cardsWithCarga);
        const { globalDemand, workTime, taktTime } = get();
        const edges: Connection[] = [];
        cardsWithCarga.forEach(card => {
          if (card.conexoes_saida) {
            edges.push(...card.conexoes_saida);
          }
        });
        const dashboardMetrics = calculateProjectMetrics(cardsWithCarga, edges.length > 0 ? edges : null, { globalDemand, workTime, taktTime });
        
        // Atualizar com cálculos completos
        const finalSheet: Sheet = {
          id: newSheetId,
          name: 'MAPA 1',
          cards: cardsWithCarga
        };
        set({ 
          sheets: [finalSheet],
          metrics,
          dashboardMetrics
        });
      }, 0);
      return;
    }
    
    // Atualizar cards IMEDIATAMENTE para UI responsiva
    const immediateSheets = sheets.map(sheet => 
      sheet.id === activeSheetId 
        ? { ...sheet, cards: [...normalizedCards] } // Nova referência com spread
        : sheet
    );
    
    set({ sheets: immediateSheets, actions: updatedActions });
    
    // Calcular métricas DEPOIS de forma assíncrona
    setTimeout(() => {
      const cardsWithVolumes = recalculateFlow(normalizedCards);
      const cardsWithCarga = updateCardsWithCarga(cardsWithVolumes);
      const metrics = calculateMetrics(cardsWithCarga);
      const { globalDemand, workTime, taktTime } = get();
      const edges: Connection[] = [];
      cardsWithCarga.forEach(card => {
        if (card.conexoes_saida) {
          edges.push(...card.conexoes_saida);
        }
      });
      const dashboardMetrics = calculateProjectMetrics(cardsWithCarga, edges.length > 0 ? edges : null, { globalDemand, workTime, taktTime });
      
      // Atualizar com cálculos completos
      const finalSheets = get().sheets.map(sheet => 
        sheet.id === activeSheetId 
          ? { ...sheet, cards: cardsWithCarga }
          : sheet
      );
      
      set({ sheets: finalSheets, metrics, dashboardMetrics });
    }, 0);
  },

  // Função atômica otimizada para adicionar um card
  addCard: (card: CardData) => {
    const { sheets, activeSheetId } = get();
    const normalizedCard = { ...card };
    
    if (!activeSheetId) {
      // Se não houver sheet ativa, criar uma com o card
      const newSheetId = generateId();
      const newSheet: Sheet = {
        id: newSheetId,
        name: 'MAPA 1',
        cards: [normalizedCard]
      };
      
      // Atualizar estado IMEDIATAMENTE
      set({ 
        sheets: [newSheet], 
        activeSheetId: newSheetId
      });
      
      // Agendar cálculos
      setTimeout(() => {
        const cards = [normalizedCard];
        const cardsWithVolumes = recalculateFlow(cards);
        const cardsWithCarga = updateCardsWithCarga(cardsWithVolumes);
        const metrics = calculateMetrics(cardsWithCarga);
        const { globalDemand, workTime, taktTime } = get();
        const edges: Connection[] = [];
        cardsWithCarga.forEach(c => {
          if (c.conexoes_saida) {
            edges.push(...c.conexoes_saida);
          }
        });
        const dashboardMetrics = calculateProjectMetrics(cardsWithCarga, edges.length > 0 ? edges : null, { globalDemand, workTime, taktTime });
        
        const finalSheet: Sheet = {
          id: newSheetId,
          name: 'MAPA 1',
          cards: cardsWithCarga
        };
        set({ 
          sheets: [finalSheet],
          metrics,
          dashboardMetrics
        });
      }, 0);
      return;
    }
    
    // Adicionar card à sheet ativa
    const immediateSheets = sheets.map(sheet => {
      if (sheet.id === activeSheetId) {
        const newCards = [...sheet.cards.filter(c => c.id !== normalizedCard.id), normalizedCard];
        return { ...sheet, cards: newCards };
      }
      return sheet;
    });
    
    // Atualizar IMEDIATAMENTE
    set({ sheets: immediateSheets });
    
    // Agendar cálculos
    setTimeout(() => {
      const currentState = get();
      const currentSheet = currentState.sheets.find(s => s.id === activeSheetId);
      if (!currentSheet) return;
      
      const cards = currentSheet.cards;
      const cardsWithVolumes = recalculateFlow(cards);
      const cardsWithCarga = updateCardsWithCarga(cardsWithVolumes);
      const metrics = calculateMetrics(cardsWithCarga);
      const { globalDemand, workTime, taktTime } = currentState;
      const edges: Connection[] = [];
      cardsWithCarga.forEach(c => {
        if (c.conexoes_saida) {
          edges.push(...c.conexoes_saida);
        }
      });
      const dashboardMetrics = calculateProjectMetrics(cardsWithCarga, edges.length > 0 ? edges : null, { globalDemand, workTime, taktTime });
      
      const finalSheets = currentState.sheets.map(sheet => 
        sheet.id === activeSheetId 
          ? { ...sheet, cards: cardsWithCarga }
          : sheet
      );
      
      set({ sheets: finalSheets, metrics, dashboardMetrics });
    }, 0);
  },

  setPostIts: (postIts) => {
    const { syncPostItToKanban, removeKanbanTaskByPostItId, postIts: currentPostIts, actions } = get();
    
    if (typeof postIts === 'function') {
      set((state) => {
        const nextPostIts = (postIts as Function)(state.postIts);
        
        // Sincronizar post-its novos ou alterados
        nextPostIts.forEach((p: PostIt) => {
          const prev = state.postIts.find(old => old.id === p.id);
          // Sincronizar sempre que for novo ou tiver mudanças relevantes; a deduplicação final
          // é tratada dentro de syncPostItToKanban para evitar re-criação silenciosa.
          if (!prev || prev.text !== p.text || prev.type !== p.type || prev.color !== p.color) {
            setTimeout(() => {
              get().syncPostItToKanban(p.id, p.type, p.text, p.color, p.fixedToNodeId);
            }, 0);
          }
        });

        // Identificar post-its removidos
        state.postIts.forEach(old => {
          if (!nextPostIts.some((p: PostIt) => p.id === old.id)) {
            setTimeout(() => {
              get().removeKanbanTaskByPostItId(old.id);
            }, 0);
          }
        });

        if (state.viewMode === 'kaizen_work' || state.isKaizenMode) {
          const session = state.optimizationSession ?? {
            id: generateId(),
            createdAt: new Date().toISOString(),
            baseMode: 'current' as const,
            baseSheetId: state.activeSheetId,
          };
          return {
            postIts: nextPostIts,
            optimizationSession: session,
            optimizationLayer: syncOptimizationLayerPostIts(state.optimizationLayer, state.postIts, nextPostIts, session.id),
          };
        }

        return { postIts: nextPostIts };
      });
    } else {
      // Sincronizar post-its novos ou alterados
      postIts.forEach((p: PostIt) => {
        const prev = currentPostIts.find(old => old.id === p.id);
        // Sincronizar sempre que for novo ou tiver mudanças relevantes; a deduplicação final
        // é tratada dentro de syncPostItToKanban para evitar re-criação silenciosa.
        if (!prev || prev.text !== p.text || prev.type !== p.type || prev.color !== p.color) {
          setTimeout(() => {
            get().syncPostItToKanban(p.id, p.type, p.text, p.color, p.fixedToNodeId);
          }, 0);
        }
      });

      // Identificar post-its removidos
      currentPostIts.forEach(old => {
        if (!postIts.some((p: PostIt) => p.id === old.id)) {
          setTimeout(() => {
            get().removeKanbanTaskByPostItId(old.id);
          }, 0);
        }
      });

      const state = get();
      if (state.viewMode === 'kaizen_work' || state.isKaizenMode) {
        const session = state.optimizationSession ?? {
          id: generateId(),
          createdAt: new Date().toISOString(),
          baseMode: 'current' as const,
          baseSheetId: state.activeSheetId,
        };
        set({
          postIts,
          optimizationSession: session,
          optimizationLayer: syncOptimizationLayerPostIts(state.optimizationLayer, currentPostIts, postIts, session.id),
        });
        return;
      }

      set({ postIts });
    }
  },

  ensureOptimizationSession: () => {
    const state = get();
    if (state.optimizationSession) return;

    const activeSheet = state.getActiveSheet();
    const cloneCards = (cards: CardData[]): CardData[] =>
      cards.map(card => ({
        ...card,
        conexoes_saida: card.conexoes_saida.map(conn => ({ ...conn })),
        resources: card.resources ? [...card.resources] : [],
        originalConexoes: card.originalConexoes
          ? card.originalConexoes.map(conn => ({ ...conn }))
          : undefined,
        vsmSupermarketOverlayOffset: card.vsmSupermarketOverlayOffset
          ? { ...card.vsmSupermarketOverlayOffset }
          : undefined,
      }));
    const clonePostIts = (items: PostIt[]): PostIt[] =>
      items.map(item => ({
        ...item,
        impactos: item.impactos ? item.impactos.map(impact => ({ ...impact })) : undefined,
        relativeOffset: item.relativeOffset ? { ...item.relativeOffset } : { x: 0, y: 0 },
      }));

    const snapshots = { ...state._modeSnapshots };
    if (activeSheet && !snapshots.current) {
      snapshots.current = {
        cards: cloneCards(activeSheet.cards),
        postIts: clonePostIts(state.postIts),
      };
    }

    set({
      optimizationSession: {
        id: generateId(),
        createdAt: new Date().toISOString(),
        baseMode: 'current',
        baseSheetId: state.activeSheetId,
      },
      optimizationLayer: [],
      _modeSnapshots: snapshots,
    });
  },

  setOptimizationLayer: (items) => set({ optimizationLayer: items }),

  addOptimizationLayerItem: (item) => {
    const state = get();
    const session = state.optimizationSession ?? {
      id: generateId(),
      createdAt: new Date().toISOString(),
      baseMode: 'current' as const,
      baseSheetId: state.activeSheetId,
    };
    const now = new Date().toISOString();
    set({
      optimizationSession: session,
      optimizationLayer: [
        ...state.optimizationLayer,
        {
          ...item,
          id: generateId(),
          sessionId: session.id,
          createdAt: now,
          updatedAt: now,
        },
      ],
    });
  },

  updatePostIt: (id: string, data: Partial<PostIt>) => {
    set((state) => {
      const updatedPostIts = state.postIts.map((p) =>
        p.id === id ? { ...p, ...data } : p
      );
      
      const updatedPostIt = updatedPostIts.find(p => p.id === id);
      if (updatedPostIt) {
        // Usar setTimeout para evitar ciclos de atualização dentro do set
        setTimeout(() => {
          get().syncPostItToKanban(
            updatedPostIt.id,
            updatedPostIt.type,
            updatedPostIt.text,
            updatedPostIt.color,
            updatedPostIt.fixedToNodeId
          );
        }, 0);
      }
      
      if (state.viewMode === 'kaizen_work' || state.isKaizenMode) {
        const session = state.optimizationSession ?? {
          id: generateId(),
          createdAt: new Date().toISOString(),
          baseMode: 'current' as const,
          baseSheetId: state.activeSheetId,
        };
        return {
          postIts: updatedPostIts,
          optimizationSession: session,
          optimizationLayer: syncOptimizationLayerPostIts(state.optimizationLayer, state.postIts, updatedPostIts, session.id),
        };
      }

      return { postIts: updatedPostIts };
    });
  },


  updateCardData: (id: string, data: Partial<CardData>) => {
    const { sheets, activeSheetId } = get();
    if (!activeSheetId) return;
    
    const activeSheet = sheets.find(s => s.id === activeSheetId);
    if (!activeSheet) return;
    
    const cards = activeSheet.cards;
    const target = cards.find(c => c.id === id);
    if (!target) return;

    const merged = { ...target, ...data };
    
    // Se resources foi alterado, atualizar pessoasSimultaneas automaticamente
    if (data.resources) {
      const totalPeople = data.resources.reduce((sum, r) => sum + (Number(r.quantity) || 0), 0);
      merged.pessoasSimultaneas = totalPeople || 0;
      // Sincronizar campo quem (string) a partir dos recursos
      const quemString = data.resources.map((r: any) => r.role || '').filter(Boolean).join(', ');
      merged.quem = quemString;
      
      // Atualizar objeto data para incluir as mudanças automáticas
      (data as any).pessoasSimultaneas = merged.pessoasSimultaneas;
      (data as any).quem = merged.quem;
    }
    
    // Calcular carga
    const newCarga = calculateCarga(merged);

    // Mesclar estilos corretamente
    let newData: Partial<CardData> = { ...data, carga: newCarga };
    if (data.style && target.style) {
      newData.style = { ...target.style, ...data.style };
    } else if (data.style) {
      newData.style = data.style;
    } else if (target.style && !data.style) {
      newData.style = target.style;
    }

    const updatedCards = cards.map(c => c.id === id ? { ...c, ...newData } : c);
    
    // REMOVIDO: Sincronização de cards verdes (usuário solicitou apenas post-its)
    let updatedActions = get().actions;

    // Recalcular volumes propagados após atualização
    const cardsWithVolumes = recalculateFlow(updatedCards);
    const cardsWithCarga = updateCardsWithCarga(cardsWithVolumes);
    
    // Atualizar sheet ativa
    const updatedSheets = sheets.map(sheet =>
      sheet.id === activeSheetId
        ? { ...sheet, cards: cardsWithCarga }
        : sheet
    );
    
    set({ sheets: updatedSheets, actions: updatedActions });
    // Recalcular métricas com simulação aplicada
    const state = get();
    const cardsWithSim = state.getCardsWithSimulation();
    const cardsWithSimAndCarga = updateCardsWithCarga(cardsWithSim);
    const metrics = calculateMetrics(cardsWithSimAndCarga, state.mapType);
    const { globalDemand, workTime, taktTime } = state;
    const edges: Connection[] = [];
    cardsWithSimAndCarga.forEach(card => {
      if (card.conexoes_saida) {
        edges.push(...card.conexoes_saida);
      }
    });
    const dashboardMetrics = calculateProjectMetrics(cardsWithSimAndCarga, edges.length > 0 ? edges : null, { globalDemand, workTime, taktTime });
    set({ metrics, dashboardMetrics });
  },
  
  recalculateFlowVolumes: () => {
    const { sheets, activeSheetId } = get();
    if (!activeSheetId) return;
    
    const activeSheet = sheets.find(s => s.id === activeSheetId);
    if (!activeSheet) return;
    
    const cardsWithVolumes = recalculateFlow(activeSheet.cards);
    const cardsWithCarga = updateCardsWithCarga(cardsWithVolumes);
    
    const updatedSheets = sheets.map(sheet =>
      sheet.id === activeSheetId
        ? { ...sheet, cards: cardsWithCarga }
        : sheet
    );
    
    set({ sheets: updatedSheets });
    // Recalcular métricas também
    const state = get();
    const cardsWithSim = state.getCardsWithSimulation();
    const cardsWithSimAndCarga = updateCardsWithCarga(cardsWithSim);
    const metrics = calculateMetrics(cardsWithSimAndCarga, state.mapType);
    const { globalDemand, workTime, taktTime } = state;
    const edges: Connection[] = [];
    cardsWithSimAndCarga.forEach(card => {
      if (card.conexoes_saida) {
        edges.push(...card.conexoes_saida);
      }
    });
    const dashboardMetrics = calculateProjectMetrics(cardsWithSimAndCarga, edges.length > 0 ? edges : null, { globalDemand, workTime, taktTime });
    set({ metrics, dashboardMetrics });
  },

  updateMultipleCardsData: (ids: string[], data: Partial<CardData>) => {
    const { sheets, activeSheetId } = get();
    if (!activeSheetId) return;
    
    const activeSheet = sheets.find(s => s.id === activeSheetId);
    if (!activeSheet) return;
    
    const cards = activeSheet.cards;
    
    const updatedCards = cards.map(c => {
      if (!ids.includes(c.id)) return c;
      
      // Criar uma cópia do card para garantir imutabilidade
      const cardCopy = { ...c };
      
      // Mesclar estilos corretamente
      let mergedStyle = cardCopy.style || {};
      if (data.style) {
        mergedStyle = { ...mergedStyle, ...data.style };
      }
      
      // Mesclar todos os dados, garantindo que campos diretos sejam atualizados
      const merged = { 
        ...cardCopy, 
        ...data,
        // Garantir que layoutColor e layoutType sejam atualizados se fornecidos
        ...(data.layoutColor !== undefined && { layoutColor: data.layoutColor }),
        ...(data.layoutType !== undefined && { layoutType: data.layoutType }),
      };
      
      // Aplicar estilo mesclado se houver
      if (data.style) {
        merged.style = mergedStyle;
      } else if (cardCopy.style) {
        // Manter estilo existente se não houver novo estilo
        merged.style = cardCopy.style;
      }
      
      // Calcular carga
      const newCarga = calculateCarga(merged);
      return { ...merged, carga: newCarga };
    });

    // REMOVIDO: Sincronização de cards verdes para ações (usuário solicitou apenas post-its)
    let updatedActions = get().actions;

    // Recalcular volumes propagados após atualização em massa
    const cardsWithVolumes = recalculateFlow(updatedCards);
    const cardsWithCarga = updateCardsWithCarga(cardsWithVolumes);
    
    // Atualizar sheet ativa
    const updatedSheets = sheets.map(sheet =>
      sheet.id === activeSheetId
        ? { ...sheet, cards: cardsWithCarga }
        : sheet
    );
    
    set({ sheets: updatedSheets, actions: updatedActions });
    // Recalcular métricas com simulação aplicada
    const newState = get();
    const cardsWithSim = newState.getCardsWithSimulation();
    const cardsWithSimAndCarga = updateCardsWithCarga(cardsWithSim);
    const metrics = calculateMetrics(cardsWithSimAndCarga);
    set({ metrics });
  },

  setSelectedCardId: (id: string | null) => {
    set({ selectedCardId: id });
  },

  setSimulatedDemand: (demand: number | null) => {
    set({ simulatedDemand: demand });
    // Recalcular métricas com simulação
    const state = get();
    const cardsWithSim = state.getCardsWithSimulation();
    const metrics = calculateMetrics(cardsWithSim);
    set({ metrics });
  },

  setSimulatedHeadcount: (headcount: number | null) => {
    set({ simulatedHeadcount: headcount });
    // Recalcular métricas com simulação
    const state = get();
    const cardsWithSim = state.getCardsWithSimulation();
    const metrics = calculateMetrics(cardsWithSim);
    set({ metrics });
  },

  
  createSimulationBaseline: () => {
    const { sheets, activeSheetId, simulationBaselineData } = get();
    if (!activeSheetId) return;
    // Poka-yoke: baseline no deve ser sobrescrito durante a mesma sesso de simulao.
    if (simulationBaselineData) return;

    const activeSheet = sheets.find((s) => s.id === activeSheetId);
    if (!activeSheet) return;

    const baselineData: Record<string, { p: number; tt: number; lt: number }> = {};
    activeSheet.cards.forEach((card) => {
      baselineData[card.id] = {
        p: Number(card.pessoasSimultaneas ?? card.p ?? 0),
        tt: Number(card.tt ?? 0),
        lt: Number(card.lt ?? card.tp ?? 0),
      };
    });

    set({ simulationBaselineData: baselineData });
  },

  resetToBaseline: () => {
    const { simulationBaselineData, sheets, activeSheetId, mapType, globalDemand, workTime, taktTime } = get();

    if (!activeSheetId) {
      set({ simulatedDemand: null, simulatedHeadcount: null, simulationBaselineData: null });
      return;
    }

    if (!simulationBaselineData) {
      set({ simulatedDemand: null, simulatedHeadcount: null });
      return;
    }

    const activeSheet = sheets.find((s) => s.id === activeSheetId);
    if (!activeSheet) {
      set({ simulatedDemand: null, simulatedHeadcount: null, simulationBaselineData: null });
      return;
    }

    const restoredCards = activeSheet.cards.map((card) => {
      const baseline = simulationBaselineData[card.id];
      if (!baseline) return card;
      return {
        ...card,
        pessoasSimultaneas: baseline.p,
        p: baseline.p,
        tt: baseline.tt,
        lt: baseline.lt,
        tp: baseline.lt,
      };
    });

    const cardsWithVolumes = recalculateFlow(restoredCards);
    const cardsWithCarga = updateCardsWithCarga(cardsWithVolumes);
    const metrics = calculateMetrics(cardsWithCarga, mapType);

    const edges: Connection[] = [];
    cardsWithCarga.forEach((card) => {
      if (card.conexoes_saida) {
        edges.push(...card.conexoes_saida);
      }
    });
    const dashboardMetrics = calculateProjectMetrics(
      cardsWithCarga,
      edges.length > 0 ? edges : null,
      { globalDemand, workTime, taktTime },
    );

    const updatedSheets = sheets.map((sheet) =>
      sheet.id === activeSheetId ? { ...sheet, cards: cardsWithCarga } : sheet,
    );

    set({
      sheets: updatedSheets,
      metrics,
      dashboardMetrics,
      simulatedDemand: null,
      simulatedHeadcount: null,
      simulationBaselineData: null,
    });
  },

  setSaveStatus: (status: 'idle' | 'saving' | 'saved' | 'error') => {
    set({ saveStatus: status });
  },

  // Actions - Sheets
  setSheets: (sheets: Sheet[], activeSheetId?: string) => {
    const newActiveSheetId = activeSheetId || sheets[0]?.id || null;
    
    // Sincronizar ações baseadas nos cards de todas as sheets
    // Para simplificar e garantir consistência, vamos verificar todas as sheets
    let updatedActions = [...get().actions];
    
    // 1. Obter todos os cards de todas as sheets (novas sheets fornecidas)
    const allCards = sheets.reduce((acc, sheet) => [...acc, ...sheet.cards], [] as CardData[]);
    const actionCards = allCards.filter(c => 
      c.tipo === 'acao' || 
      c.layoutColor === 'emerald' || 
      c.layoutColor === 'green' ||
      (c.style?.backgroundColor && (
        c.style.backgroundColor.toLowerCase().includes('#bbf7d0') || 
        c.style.backgroundColor.toLowerCase().includes('#f0fdf4') ||
        c.style.backgroundColor.toLowerCase().includes('#dcfce7') ||
        c.style.backgroundColor.toLowerCase().includes('#86efac') ||
        c.style.backgroundColor.toLowerCase().includes('green')
      ))
    );
    
    // 2. Identificar ações que devem ser removidas (cards que sumiram de TODAS as sheets)
    updatedActions = updatedActions.filter(action => {
      if (action.origin === 'canvas' || action.linkedNodeId) {
        const linkedId = action.linkedNodeId || action.id;
        return allCards.some(c => c.id === linkedId);
      }
      return true;
    });
    
    // 3. Adicionar ou atualizar ações baseadas em todos os actionCards encontrados
    actionCards.forEach(card => {
      const existingActionIndex = updatedActions.findIndex(a => a.id === card.id || a.linkedNodeId === card.id);
      if (existingActionIndex !== -1) {
        const existingAction = updatedActions[existingActionIndex];
        if (existingAction.title !== card.atividade || existingAction.status !== card.actionStatus) {
          updatedActions[existingActionIndex] = { 
            ...existingAction, 
            title: card.atividade || existingAction.title, 
            status: card.actionStatus || existingAction.status, 
            updatedAt: new Date().toISOString() 
          };
        }
      } else {
        updatedActions.push({
          id: card.id,
          linkedNodeId: card.id,
          title: card.atividade || 'Nova Ação',
          status: card.actionStatus || 'todo',
          origin: 'canvas',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
      }
    });

    set({ sheets, activeSheetId: newActiveSheetId, actions: updatedActions });
    
    // Recalcular métricas da sheet ativa E ATUALIZAR CARDS COM VOLUMES/CARGA
    if (newActiveSheetId) {
      const activeSheet = sheets.find(s => s.id === newActiveSheetId);
      if (activeSheet) {
        const cardsWithVolumes = recalculateFlow(activeSheet.cards);
        const cardsWithCarga = updateCardsWithCarga(cardsWithVolumes);
        
        // CRÍTICO: Atualizar sheets com os cards que têm incomingVolume e carga calculados
        const updatedSheets = sheets.map(s => 
          s.id === newActiveSheetId
            ? { ...s, cards: cardsWithCarga }
            : s
        );
        
        // Recalcular métricas completas (incluindo dashboardMetrics)
        const state = get();
        const cardsWithSim = cardsWithCarga; // Já tem volumes e carga
        const metrics = calculateMetrics(cardsWithSim, state.mapType);
        const { globalDemand, workTime, taktTime } = state;
        const edges: Connection[] = [];
        cardsWithSim.forEach(card => {
          if (card.conexoes_saida) {
            edges.push(...card.conexoes_saida);
          }
        });
        const dashboardMetrics = calculateProjectMetrics(cardsWithSim, edges.length > 0 ? edges : null, { globalDemand, workTime, taktTime });
        
        set({ sheets: updatedSheets, metrics, dashboardMetrics });
      }
    }
  },
  
  addSheet: (name: string) => {
    const { sheets } = get();
    const newSheetId = generateId();
    const newSheet: Sheet = {
      id: newSheetId,
      name: name || `Fluxo ${sheets.length + 1}`,
      cards: []
    };
    set({ 
      sheets: [...sheets, newSheet],
      activeSheetId: newSheetId
    });
    return newSheetId;
  },
  
  setActiveSheet: (id: string) => {
    const { sheets } = get();
    const sheet = sheets.find(s => s.id === id);
    if (!sheet) return;
    
    set({ activeSheetId: id });
    
    // Recalcular métricas da nova sheet ativa
    const cardsWithVolumes = recalculateFlow(sheet.cards);
    const cardsWithCarga = updateCardsWithCarga(cardsWithVolumes);
    const metrics = calculateMetrics(cardsWithCarga, get().mapType);
    set({ metrics });
  },
  
  duplicateSheet: (id: string) => {
    const { sheets } = get();
    const sourceSheet = sheets.find(s => s.id === id);
    if (!sourceSheet) return id;
    
    // Deep copy dos cards com novos IDs
    const idMap = new Map<string, string>(); // Mapeia IDs antigos para novos
    
    const duplicatedCards = sourceSheet.cards.map(card => {
      const newId = generateId();
      idMap.set(card.id, newId);
      
      // Criar cópia profunda do card
      const newCard: CardData = {
        ...card,
        id: newId,
        // Atualizar conexões de saída com novos IDs
        conexoes_saida: card.conexoes_saida.map(conn => {
          const newTargetId = idMap.get(conn.targetId) || conn.targetId;
          return {
            ...conn,
            id: generateId(),
            targetId: newTargetId
          };
        })
      };
      
      return newCard;
    });
    
    // Atualizar conexões que apontam para cards dentro da sheet
    const finalCards = duplicatedCards.map(card => {
      const updatedConnections = card.conexoes_saida.map(conn => {
        // Se o target está na sheet duplicada, usar o novo ID
        const newTargetId = idMap.get(conn.targetId);
        if (newTargetId) {
          return { ...conn, targetId: newTargetId };
        }
        return conn;
      });
      return { ...card, conexoes_saida: updatedConnections };
    });
    
    const newSheetId = generateId();
    const newSheet: Sheet = {
      id: newSheetId,
      name: `Cópia de ${sourceSheet.name}`,
      cards: finalCards,
      viewport: sourceSheet.viewport ? { ...sourceSheet.viewport } : undefined,
      color: sourceSheet.color
    };
    
    set({ 
      sheets: [...sheets, newSheet],
      activeSheetId: newSheetId
    });
    
    return newSheetId;
  },
  
  renameSheet: (id: string, newName: string) => {
    const { sheets } = get();
    const updatedSheets = sheets.map(sheet =>
      sheet.id === id ? { ...sheet, name: newName } : sheet
    );
    set({ sheets: updatedSheets });
  },
  
  deleteSheet: (id: string) => {
    const { sheets, activeSheetId } = get();
    if (sheets.length <= 1) {
      // Não permitir deletar a última sheet
      return;
    }
    
    const updatedSheets = sheets.filter(s => s.id !== id);
    const newActiveSheetId = activeSheetId === id 
      ? (updatedSheets[0]?.id || null)
      : activeSheetId;
    
    set({ 
      sheets: updatedSheets,
      activeSheetId: newActiveSheetId
    });
    
    // Recalcular métricas da nova sheet ativa
    if (newActiveSheetId) {
      const activeSheet = updatedSheets.find(s => s.id === newActiveSheetId);
      if (activeSheet) {
        const cardsWithVolumes = recalculateFlow(activeSheet.cards);
        const cardsWithCarga = updateCardsWithCarga(cardsWithVolumes);
        const metrics = calculateMetrics(cardsWithCarga, get().mapType);
        set({ metrics });
      }
    }
  },
  
  updateSheetViewport: (id: string, viewport: Viewport) => {
    const { sheets } = get();
    const updatedSheets = sheets.map(sheet =>
      sheet.id === id ? { ...sheet, viewport } : sheet
    );
    set({ sheets: updatedSheets });
  },
  
  updateSheetColor: (id: string, color: string) => {
    const { sheets } = get();
    const updatedSheets = sheets.map(sheet =>
      sheet.id === id ? { ...sheet, color } : sheet
    );
    set({ sheets: updatedSheets });
  },
  
  // Actions - Organization
  setOrganization: (organization: Organization) => {
    // Garantir que sempre tenha pelo menos os departamentos padrão
    if (!organization || !organization.departments || organization.departments.length === 0) {
      organization = createDefaultOrganization();
    }
    set({ organization });
    // Atualizar cores dos cards quando setor muda de cor
    const { sheets, activeSheetId } = get();
    if (activeSheetId) {
      const activeSheet = sheets.find(s => s.id === activeSheetId);
      if (activeSheet) {
        const updatedCards = activeSheet.cards.map(card => {
          if (card.resources && card.resources.length > 0) {
            // Verificar se algum recurso do card pertence a um setor que mudou de cor
            const updatedResources = card.resources.map(resource => {
              const role = organization.roles.find(r => r.id === resource.roleId);
              if (role) {
                const department = organization.departments.find(d => d.id === role.departmentId);
                if (department) {
                  // Atualizar layoutColor do card com a cor do setor
                  return resource;
                }
              }
              return resource;
            });
            return { ...card, resources: updatedResources };
          }
          return card;
        });
        const updatedSheets = sheets.map(sheet =>
          sheet.id === activeSheetId ? { ...sheet, cards: updatedCards } : sheet
        );
        set({ sheets: updatedSheets });
      }
    }
  },
  
  addSectorSuggestion: (name: string) => {
    const { sectorSuggestions, organization } = get();
    const alreadyOfficial = organization.departments.some(d => d.name.toLowerCase() === name.toLowerCase());
    const alreadySuggested = sectorSuggestions.some(s => s.toLowerCase() === name.toLowerCase());
    if (!alreadyOfficial && !alreadySuggested && name.trim()) {
      set({ sectorSuggestions: [...sectorSuggestions, name.trim()] });
    }
  },

  removeSectorSuggestion: (name: string) => {
    set((state) => ({ sectorSuggestions: state.sectorSuggestions.filter(s => s !== name) }));
  },

  promoteSectorSuggestion: (name: string) => {
    const { addDepartment, removeSectorSuggestion } = get();
    const newId = addDepartment({ name, color: '#64748B', visible: true, totalHeadcount: 0, shiftMinutes: 480, shiftsPerDay: 1 });
    removeSectorSuggestion(name);
    return newId;
  },

  addDepartment: (department: Omit<Department, 'id'>) => {
    const { organization } = get();
    const newId = generateId();
    const newDepartment: Department = { ...department, id: newId };
    set({
      organization: {
        ...organization,
        departments: [...organization.departments, newDepartment]
      }
    });
    return newId;
  },
  
  updateDepartment: (id: string, data: Partial<Department>) => {
    const { organization, sheets, activeSheetId } = get();
    const updatedDepartments = organization.departments.map(dept =>
      dept.id === id ? { ...dept, ...data } : dept
    );
    
    set({
      organization: {
        ...organization,
        departments: updatedDepartments
      }
    });
    
    // Se a cor mudou, atualizar cards que usam roles deste setor
    if (data.color && activeSheetId) {
      const activeSheet = sheets.find(s => s.id === activeSheetId);
      if (activeSheet) {
        const rolesInDepartment = organization.roles.filter(r => r.departmentId === id);
        const updatedCards = activeSheet.cards.map(card => {
          if (card.resources && card.resources.length > 0) {
            const hasRoleFromDepartment = card.resources.some(resource =>
              rolesInDepartment.some(role => role.id === resource.roleId)
            );
            if (hasRoleFromDepartment) {
              // Atualizar layoutColor com a nova cor do setor
              return { ...card, layoutColor: data.color };
            }
          }
          return card;
        });
        const updatedSheets = sheets.map(sheet =>
          sheet.id === activeSheetId ? { ...sheet, cards: updatedCards } : sheet
        );
        set({ sheets: updatedSheets });
      }
    }
  },
  
  deleteDepartment: (id: string) => {
    const { organization } = get();
    // Não permitir deletar se houver roles associados
    const rolesInDepartment = organization.roles.filter(r => r.departmentId === id);
    if (rolesInDepartment.length > 0) {
      throw new Error('Não é possível excluir um departamento que possui cargos associados');
    }
    
    set({
      organization: {
        ...organization,
        departments: organization.departments.filter(d => d.id !== id)
      }
    });
  },
  
  addRole: (role: Omit<OrgRole, 'id'>) => {
    const { organization } = get();
    const newId = generateId();
    const newRole: OrgRole = { ...role, id: newId, currency: role.currency || 'BRL' };
    set({
      organization: {
        ...organization,
        roles: [...organization.roles, newRole]
      }
    });
    return newId;
  },
  
  updateRole: (id: string, data: Partial<OrgRole>) => {
    const { organization, sheets, activeSheetId } = get();
    const updatedRoles = organization.roles.map(role =>
      role.id === id ? { ...role, ...data } : role
    );
    
    set({
      organization: {
        ...organization,
        roles: updatedRoles
      }
    });
    
    // Se costPerHour mudou, atualizar cards que usam este role
    if (data.costPerHour !== undefined && activeSheetId) {
      const activeSheet = sheets.find(s => s.id === activeSheetId);
      if (activeSheet) {
        const updatedCards = activeSheet.cards.map(card => {
          if (card.resources && card.resources.length > 0) {
            const updatedResources = card.resources.map(resource => {
              if (resource.roleId === id) {
                return { ...resource, costPerHour: data.costPerHour };
              }
              return resource;
            });
            return { ...card, resources: updatedResources };
          }
          return card;
        });
        const updatedSheets = sheets.map(sheet =>
          sheet.id === activeSheetId ? { ...sheet, cards: updatedCards } : sheet
        );
        set({ sheets: updatedSheets });
      }
    }
  },
  
  deleteRole: (id: string) => {
    const { organization, sheets, activeSheetId } = get();
    // Remover role de cards que o utilizam
    if (activeSheetId) {
      const activeSheet = sheets.find(s => s.id === activeSheetId);
      if (activeSheet) {
        const updatedCards = activeSheet.cards.map(card => {
          if (card.resources && card.resources.length > 0) {
            const updatedResources = card.resources.filter(resource => resource.roleId !== id);
            return { ...card, resources: updatedResources };
          }
          return card;
        });
        const updatedSheets = sheets.map(sheet =>
          sheet.id === activeSheetId ? { ...sheet, cards: updatedCards } : sheet
        );
        set({ sheets: updatedSheets });
      }
    }
    
    set({
      organization: {
        ...organization,
        roles: organization.roles.filter(r => r.id !== id)
      }
    });
  },
  
  getRoleById: (id: string) => {
    const { organization } = get();
    if (!organization || !organization.roles) return undefined;
    return organization.roles.find(r => r.id === id);
  },
  
  getDepartmentById: (id: string) => {
    const { organization } = get();
    if (!organization || !organization.departments) return undefined;
    return organization.departments.find(d => d.id === id);
  },
  
  getRolesByDepartment: (departmentId: string) => {
    const { organization } = get();
    if (!organization || !organization.roles) return [];
    return organization.roles.filter(r => r.departmentId === departmentId);
  },
  
  // Computed
  getActiveSheet: () => {
    const { sheets, activeSheetId } = get();
    return sheets.find(s => s.id === activeSheetId);
  },
  
  getCards: () => {
    const { sheets, activeSheetId } = get();
    if (!activeSheetId) return [];
    const activeSheet = sheets.find(s => s.id === activeSheetId);
    // Retornar cópia para garantir imutabilidade
    return activeSheet?.cards ? [...activeSheet.cards] : [];
  },
  
  getSelectedCard: () => {
    const { selectedCardId } = get();
    const cards = get().getCards();
    return cards.find(c => c.id === selectedCardId);
  },

  getCardsWithSimulation: () => {
    const { simulatedDemand, simulatedHeadcount, isKaizenMode, postIts } = get();
    const cards = get().getCards();
    
    // Se não há simulação de demanda/headcount E não está em Modo Kaizen, retornar cards originais
    if (simulatedDemand === null && simulatedHeadcount === null && !isKaizenMode) {
      return cards;
    }

    return cards.map(card => {
      let newCard = { ...card };

      // Ajustar demanda no card inicial (simulação de demanda)
      if (newCard.tipo === 'card_inicial' && simulatedDemand !== null) {
        newCard.entrada = simulatedDemand;
      }

      // Aplicar impactos do Modo Kaizen
      if (isKaizenMode) {
        // Buscar todos os post-its de ação que impactam este card
        const actionPostIts = postIts.filter(p => 
          p.impactos && 
          p.impactos.length > 0 &&
          p.impactos.some(impacto => impacto.targetNodeId === card.id)
        );

        // Aplicar cada impacto
        actionPostIts.forEach(postIt => {
          postIt.impactos?.forEach(impacto => {
            if (impacto.targetNodeId === card.id) {
              // Salvar valores originais se ainda não foram salvos
              if (newCard.originalTP === undefined) {
                newCard.originalTP = newCard.lt || 0;
              }
              if (newCard.originalTRA === undefined) {
                newCard.originalTRA = newCard.tt || 0;
              }
              
              if (impacto.tipo === 'ELIMINAR') {
                // ELIMINAR: zera TRA e TP
                newCard.tt = 0;
                newCard.lt = 0;
              } else if (impacto.tipo === 'TRA') {
                // TRA: reduz Tempo de Repasse/Agregação (tt)
                const reducao = impacto.reducao / 100;
                newCard.tt = Math.max(0, (newCard.tt || 0) * (1 - reducao));
              } else if (impacto.tipo === 'TP') {
                // TP: reduz Tempo de Processamento (lt)
                const reducao = impacto.reducao / 100;
                newCard.lt = Math.max(0, (newCard.lt || 0) * (1 - reducao));
              }
            }
          });
        });
      }

      return newCard;
    });
  },
  
  // Actions - Post-its View Mode
  setPostItsViewMode: (mode: 'hidden' | 'compact' | 'detailed') => {
    const current = get().postItsViewMode;
    // Se está mudando de compact/detailed para hidden, salvar o modo anterior
    if ((current === 'compact' || current === 'detailed') && mode === 'hidden') {
      set({ postItsViewMode: mode, previousPostItsViewMode: current });
    } else {
      set({ postItsViewMode: mode });
    }
  },
  
  togglePostItsVisibility: () => {
    const current = get().postItsViewMode;
    const previous = get().previousPostItsViewMode || 'detailed';
    if (current === 'hidden') {
      // Se está oculto, restaurar o modo anterior (compact ou detailed)
      set({ postItsViewMode: previous });
    } else {
      // Se está visível, ocultar e salvar o modo atual
      set({ postItsViewMode: 'hidden', previousPostItsViewMode: current === 'compact' ? 'compact' : 'detailed' });
    }
  },
  
  minimizePostIts: () => {
    const current = get().postItsViewMode;
    // Toggle entre compact (miniaturas) e detailed (normal)
    if (current === 'hidden') {
      // Se está oculto, restaurar o modo anterior e depois fazer toggle
      const previous = get().previousPostItsViewMode || 'detailed';
      // Se o modo anterior era detailed, mudar para compact; se era compact, mudar para detailed
      const newMode = previous === 'detailed' ? 'compact' : 'detailed';
      set({ postItsViewMode: newMode, previousPostItsViewMode: previous });
    } else if (current === 'compact') {
      // Se está em compact (miniaturas), mudar para detailed (normal)
      set({ postItsViewMode: 'detailed' });
    } else {
      // Se está em detailed (normal), mudar para compact (miniaturas)
      set({ postItsViewMode: 'compact' });
    }
  },

  // Action para adicionar post-it a um card específico
  // IMPORTANTE: Esta função garante atualização imutável para forçar re-renderização do React Flow
  // Como os post-its são renderizados dentro do ActivityNode baseado em linkedPostIts,
  // esta função força uma atualização imutável do card para garantir que o React detecte a mudança
  addPostIt: (cardId: string, postIt: { id: string; type: string; text: string; x: number; y: number; fixedToNodeId: string | null; relativeOffset: { x: number; y: number }; color: string; isMinimized?: boolean }) => {
    const { sheets, activeSheetId } = get();
    if (!activeSheetId) return;
    
    // Usar padrão imutável estrito: criar novos objetos em todos os níveis
    // Isso FOR!A O REACT A PERCEBER A MUDAN!A mesmo que os dados internos sejam os mesmos
    set((state) => {
      const updatedSheets = state.sheets.map((sheet) => {
        if (sheet.id !== activeSheetId) return sheet;
        
        // Criar novo array de cards com atualização imutável
        const updatedCards = sheet.cards.map((card) => {
          if (card.id !== cardId) return card;
          
          // FOR!A O REACT A PERCEBER A MUDAN!A
          // Criar um novo objeto card com todas as propriedades copiadas
          // Mesmo que os dados sejam os mesmos, a nova referência de objeto força o re-render
          return {
            ...card, // Copia todas as propriedades do card
            // Criar cópias imutáveis de objetos aninhados para garantir detecção de mudança
            posicao: { ...card.posicao },
            conexoes_saida: card.conexoes_saida.map(conn => ({ ...conn })),
            // Se houver style, criar nova referência também
            ...(card.style && { style: { ...card.style } }),
            // Se houver resources, criar nova referência
            ...(card.resources && { resources: card.resources.map(res => ({ ...res })) }),
            // Se houver desperdicios, criar nova referência
            ...(card.desperdicios && { desperdicios: [...card.desperdicios] }),
          };
        });
        
        // Retornar novo objeto sheet com novo array de cards
        return {
          ...sheet,
          cards: updatedCards
        };
      });
      
      return {
        sheets: updatedSheets
      };
    });
  },
  
  // Actions - Kanban
  toggleKanban: () => {
    set((state) => ({ isKanbanOpen: !state.isKanbanOpen }));
  },
  
  // Actions - Kaizen Mode
  toggleKaizenMode: () => {
    set((state) => ({ isKaizenMode: !state.isKaizenMode }));
  },
  
  // Sincronização Post-It -> Kanban: Adiciona/atualiza tarefa se for tipo 'action' ou cor verde
  syncPostItToKanban: (postItId: string, postItType: string, postItText: string, postItColor?: string, linkedNodeId?: string | null) => {
    const { actions, currentProjectId, actionPlans } = get();
    const normalize = (value?: string) =>
      String(value ?? '')
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
    const normalizedPostItTitle = normalize(postItText || 'Nova ação');
    
    // Verificar se é do tipo 'action' ou tem cor verde
    const isAction = postItType === 'action' || (postItColor && (
      postItColor.toLowerCase().includes('#bbf7d0') ||
      postItColor.toLowerCase().includes('#f0fdf4') ||
      postItColor.toLowerCase().includes('#dcfce7') ||
      postItColor.toLowerCase().includes('#86efac') ||
      postItColor.toLowerCase().includes('green') ||
      postItColor.toLowerCase().includes('#6ee7b7') // emerald-300 atual
    ));
    
    if (isAction) {
      // Adicionar ou atualizar na lista de ações global (ActionHub/Memo)
      const existingAction = actions.find((a) => a.linkedPostItId === postItId)
        ?? actions.find((a) => {
          if (currentProjectId && a.projectId && a.projectId !== currentProjectId) return false;
          if (normalize(a.title) !== normalizedPostItTitle) return false;
          if (linkedNodeId && a.linkedNodeId && a.linkedNodeId !== linkedNodeId) return false;
          return true;
        });
      
      if (existingAction) {
        // Atualizar ação existente
        set((state) => ({
          actions: state.actions.map((a) =>
            (a.id === existingAction.id)
              ? {
                  ...a,
                  title: postItText || a.title,
                  linkedPostItId: a.linkedPostItId ?? postItId,
                  linkedNodeId: a.linkedNodeId ?? linkedNodeId ?? undefined,
                  updatedAt: new Date().toISOString(),
                }
              : a
          ),
        }));
      } else {
        // Criar nova ação
        const newId = generateId();
        const now = new Date().toISOString();
        
        // Tentar encontrar um plano de ação padrão para o projeto
        const defaultPlan = actionPlans.find(p => p.name.includes('Plano de Ação') || p.name.includes('Kaizen')) || actionPlans[0];
        
        const newAction: Action = {
          id: newId,
          title: postItText || 'Nova ação',
          status: 'todo',
          linkedNodeId: linkedNodeId || null, // Vincular ao card onde o post-it está fixado
          linkedPostItId: postItId, // Manter referência ao post-it para sincronização bidirecional
          origin: 'canvas',
          projectId: currentProjectId || undefined,
          actionPlanId: defaultPlan?.id,
          createdAt: now,
          updatedAt: now,
        };
        
        set((state) => ({
          actions: [...state.actions, newAction],
          // Atualizar o plano de ação se um foi selecionado
          actionPlans: defaultPlan
            ? state.actionPlans.map(p => p.id === defaultPlan.id ? { ...p, actionIds: [...p.actionIds, newId] } : p)
            : state.actionPlans
        }));
      }
    } else {
      // Se NÒO for action, remover das ações (caso exista e tenha sido criada via post-it)
      const actionToRemove = actions.find((a) => a.linkedPostItId === postItId);
      if (actionToRemove) {
        set((state) => {
          const updatedActions = state.actions.filter((a) => a.id !== actionToRemove.id);
          const updatedPlans = state.actionPlans.map(p => ({
            ...p,
            actionIds: p.actionIds.filter(id => id !== actionToRemove.id)
          }));
          return { actions: updatedActions, actionPlans: updatedPlans };
        });
      }
    }
  },
  
  // Remover tarefa do Kanban quando post-it é deletado
  removeKanbanTaskByPostItId: (postItId: string) => {
    set((state) => {
      const actionToRemove = state.actions.find(a => a.linkedPostItId === postItId);
      const updatedActions = state.actions.filter((a) => a.linkedPostItId !== postItId);
      const updatedPlans = state.actionPlans.map(p => ({
        ...p,
        actionIds: (p.actionIds || []).filter(id => id !== actionToRemove?.id)
      }));
      return {
        kanbanTasks: (state.kanbanTasks ?? []).filter((task) => task.linkedNodeId !== postItId),
        actions: updatedActions,
        actionPlans: updatedPlans
      };
    });
  },
  
  // Atualizar post-it quando tarefa do Kanban é editada (bidirecional)
  updateKanbanTaskLinkedPostIt: (taskId: string, newText: string, updatePostItCallback?: (postItId: string, text: string) => void) => {
    const { kanbanTasks } = get();
    const task = (kanbanTasks ?? []).find((t) => t.id === taskId);
    if (task && task.linkedNodeId && updatePostItCallback) {
      updatePostItCallback(task.linkedNodeId, newText);
    }
  },
  
  addKanbanTask: (task: Omit<KanbanTask, 'id' | 'createdAt' | 'updatedAt'>) => {
    const newId = generateId();
    const now = new Date().toISOString();
    const newTask: KanbanTask = {
      ...task,
      id: newId,
      createdAt: now,
      updatedAt: now,
    };
    set((state) => ({
      kanbanTasks: [...(state.kanbanTasks ?? []), newTask],
    }));
    return newId;
  },
  
  updateKanbanTask: (id: string, data: Partial<KanbanTask>, updatePostItCallback?: (postItId: string, text: string) => void) => {
    const { kanbanTasks } = get();
    const task = (kanbanTasks ?? []).find((t) => t.id === id);
    
    set((state) => ({
      kanbanTasks: (state.kanbanTasks ?? []).map((task) =>
        task.id === id
          ? { ...task, ...data, updatedAt: new Date().toISOString() }
          : task
      ),
    }));
    
    // Se o texto foi atualizado e há um post-it vinculado, atualizar o post-it também
    if (data.title && task?.linkedNodeId && updatePostItCallback) {
      updatePostItCallback(task.linkedNodeId, data.title);
    }
  },
  
  moveKanbanTask: (id: string, newStatus: KanbanTaskStatus) => {
    set((state) => ({
      kanbanTasks: (state.kanbanTasks ?? []).map((task) =>
        task.id === id
          ? { ...task, status: newStatus, updatedAt: new Date().toISOString() }
          : task
      ),
    }));
  },
  
  deleteKanbanTask: (id: string) => {
    set((state) => ({
      kanbanTasks: (state.kanbanTasks ?? []).filter((task) => task.id !== id),
    }));
  },
  
  // Actions - Widget Lateral
  toggleWidget: () => {
    set((state) => ({ isWidgetOpen: !state.isWidgetOpen }));
  },
  
  expandDashboard: () => {
    // Fecha o widget e abre o modal full
    set({ isWidgetOpen: false, isFullDashboardOpen: true });
  },
  
  // Actions - Dashboard Completo (Modal)
  toggleFullDashboard: () => {
    set((state) => ({ isFullDashboardOpen: !state.isFullDashboardOpen }));
  },
  
  recalculateDashboardMetrics: () => {
    const { sheets, activeSheetId, globalDemand, workTime, taktTime } = get();
    if (!activeSheetId) {
      set({ dashboardMetrics: {
        totalCost: 0,
        totalFTE: 0,
        bottleneckId: null,
        bottleneckName: null,
        leadTime: 0,
        efficiency: 0,
      } });
      return;
    }
    
    const activeSheet = sheets.find(s => s.id === activeSheetId);
    if (!activeSheet || !activeSheet.cards || activeSheet.cards.length === 0) {
      set({ dashboardMetrics: {
        totalCost: 0,
        totalFTE: 0,
        bottleneckId: null,
        bottleneckName: null,
        leadTime: 0,
        efficiency: 0,
      } });
      return;
    }
    
    // Extrair edges das conexões
    const edges: Connection[] = [];
    activeSheet.cards.forEach(card => {
      if (card.conexoes_saida) {
        edges.push(...card.conexoes_saida);
      }
    });
    
    const dashboardMetrics = calculateProjectMetrics(
      activeSheet.cards,
      edges.length > 0 ? edges : null,
      { globalDemand, workTime, taktTime }
    );
    
    set({ dashboardMetrics });
  },
  
  // Actions - Análise de Gargalos (Fase 3.2)
  setSimulationParams: (demand: number, time: number) => {
    if (demand <= 0 || time <= 0) {
      return;
    }
    const calculatedTaktTime = time / demand;
    set({
      globalDemand: demand,
      workTime: time,
      taktTime: calculatedTaktTime,
    });
  },
  
  // Migração
  migrateLegacyState: (legacyCards: CardData[]) => {
    const { sheets } = get();
    
    // Se já houver sheets, não migrar
    if (sheets.length > 0) return;
    
    // Se não houver cards legados, criar sheet vazia
    if (!legacyCards || legacyCards.length === 0) {
      const newSheetId = generateId();
      const newSheet: Sheet = {
        id: newSheetId,
        name: 'MAPA 1',
        cards: []
      };
      set({ sheets: [newSheet], activeSheetId: newSheetId });
      return;
    }
    
    // Migrar cards legados para uma nova sheet
    const newSheetId = generateId();
    const cardsWithVolumes = recalculateFlow(legacyCards);
    const cardsWithCarga = updateCardsWithCarga(cardsWithVolumes);
    const metrics = calculateMetrics(cardsWithCarga, get().mapType);
    
    const newSheet: Sheet = {
      id: newSheetId,
      name: 'MAPA 1',
      cards: cardsWithCarga
    };
    
    set({ 
      sheets: [newSheet], 
      activeSheetId: newSheetId,
      metrics 
    });
  },
  
  // Actions - Action Plans
  addActionPlan: (plan) => {
    const newId = generateId();
    const now = new Date().toISOString();
    const newActionPlan: ActionPlan = {
      ...plan,
      id: newId,
      actionIds: [],
      createdAt: now,
      updatedAt: now,
    };
    set((state) => ({
      actionPlans: [...state.actionPlans, newActionPlan],
    }));
    return newId;
  },
  
  updateActionPlan: (id, data) => {
    set((state) => ({
      actionPlans: state.actionPlans.map((ap) =>
        ap.id === id ? { ...ap, ...data, updatedAt: new Date().toISOString() } : ap
      ),
    }));
  },
  
  deleteActionPlan: (id) => {
    set((state) => ({
      actionPlans: state.actionPlans.filter((ap) => ap.id !== id),
      actions: state.actions.filter((a) => a.actionPlanId !== id),
    }));
  },
  
  getActionPlan: (id) => get().actionPlans.find((ap) => ap.id === id),
  
  getActionPlans: () => get().actionPlans,
  
  // Actions - Actions
  addAction: (action) => {
    const newId = generateId();
    const now = new Date().toISOString();
    const newAction: Action = {
      ...action,
      id: newId,
      createdAt: now,
      updatedAt: now,
      status: action.status || 'todo',
    };
    
    set((state) => {
      const updatedActions = [...state.actions, newAction];
      
      // Sincronizar com o mapa: Criar ou atualizar POST-IT conforme necessário
      const { sheets, activeSheetId, postIts } = state;
      
      let updatedPostIts = postIts;
      
      // Se a ação está vinculada a um post-it existente, atualizar o post-it com o texto "O que"
      if (action.linkedPostItId) {
        const postItExists = postIts.find(p => p.id === action.linkedPostItId);
        if (postItExists) {
          updatedPostIts = postIts.map(p => {
            if (p.id === action.linkedPostItId) {
              return { 
                ...p, 
                text: action.title || p.text, 
                type: 'action', 
                color: '#bbf7d0',
                // Garantir que o linkedActionId seja definido para sincronização
                linkedActionId: newId
              };
            }
            return p;
          });
        }
      } else if (action.linkedNodeId) {
        // Se linkedNodeId existe mas não linkedPostItId   ação criada no mapa, criar post-it verde no card
        const linkedCard = sheets.find(s => s.id === activeSheetId)?.cards.find(c => c.id === action.linkedNodeId);
        if (linkedCard) {
          // Verificar se já existe um post-it de ação vinculado a este card
          const existingPostIt = postIts.find(p => p.fixedToNodeId === action.linkedNodeId && p.type === 'action');
          
          if (existingPostIt) {
            // Atualizar post-it existente com o texto "O que"
            updatedPostIts = postIts.map(p => 
              p.id === existingPostIt.id 
                ? { ...p, text: action.title || p.text, type: 'action', color: '#bbf7d0' }
                : p
            );
          } else {
            // Criar novo post-it verde no card usando layout automático
            const linkedPostIts = postIts.filter(p => p.fixedToNodeId === action.linkedNodeId);
            const cardWidth = linkedCard.tipo === 'decisao' ? 160 : 240;
            const cardHeight = linkedCard.tipo === 'decisao' ? 160 : 160;
            const totalAfterAdd = linkedPostIts.length + 1;
            
            // Usar função de layout automático
            const layout = calculatePostItAutoLayout(
              linkedCard.posicao,
              cardWidth,
              cardHeight,
              linkedPostIts.length,
              totalAfterAdd
            );

            const newPostIt: PostIt = {
              id: newId, // Usar o mesmo ID da ação para facilitar sincronização
              type: 'action',
              text: action.title || 'Nova Ação',
              x: layout.x,
              y: layout.y,
              fixedToNodeId: action.linkedNodeId,
              relativeOffset: layout.relativeOffset,
              color: '#bbf7d0', // Verde esmeralda para ações
              order: linkedPostIts.length,
            };

            updatedPostIts = [...postIts, newPostIt];
          }
        }
      } else {
        // Se nenhum dos dois existe   ação criada no memo, criar post-it verde livre no mapa
        let newPostItX = 100;
        let newPostItY = 100;
        let fixedToNodeId: string | null = null;

        // Canto esquerdo inferior do mapa
        const activeCards = sheets.find(s => s.id === activeSheetId)?.cards || [];
        if (activeCards.length > 0) {
          let minX = Infinity;
          let maxY = -Infinity;
          activeCards.forEach(c => {
            minX = Math.min(minX, c.posicao.x);
            maxY = Math.max(maxY, c.posicao.y);
          });
          newPostItX = minX;
          newPostItY = maxY + 200; // Posicionar abaixo do card mais baixo
        }

        const newPostIt: PostIt = {
          id: newId, // Usar o mesmo ID para facilitar sincronização
          type: 'action',
          text: action.title || 'Nova Ação',
          x: newPostItX,
          y: newPostItY,
          fixedToNodeId: fixedToNodeId,
          relativeOffset: { x: 0, y: 0 },
          color: '#bbf7d0', // Emerald default
        };

        updatedPostIts = [...postIts, newPostIt];
      }

      if (newAction.actionPlanId) {
        const updatedActionPlans = state.actionPlans.map((ap) =>
          ap.id === newAction.actionPlanId
            ? { ...ap, actionIds: [...ap.actionIds, newId], updatedAt: now }
            : ap
        );
        return { actions: updatedActions, actionPlans: updatedActionPlans, postIts: updatedPostIts };
      }
      return { actions: updatedActions, postIts: updatedPostIts };
    });
    return newId;
  },

  // x} Criar ação diretamente a partir de um Post-it
  addActionFromPostIt: (postItId, text, linkedNodeId = null) => {
    const state = get();
    const postIt = state.postIts.find(p => p.id === postItId);
    
    if (!postIt) {
      return '';
    }

    // Criar ação vinculada ao post-it
    const newId = generateId();
    const now = new Date().toISOString();
    const newAction: Action = {
      id: newId,
      title: text || postIt.text || 'Nova Ação',
      description: '',
      projectId: state.currentProjectId || '',
      status: 'todo',
      origin: 'canvas',
      linkedPostItId: postItId,
      linkedNodeId: linkedNodeId || postIt.fixedToNodeId || null,
      createdAt: now,
      updatedAt: now,
    };

    set((state) => {
      // Adicionar ação
      const updatedActions = [...state.actions, newAction];
      
      // Marcar post-it como vinculado à ação
      const updatedPostIts = state.postIts.map(p => 
        p.id === postItId 
          ? { ...p, linkedActionId: newId, type: 'action', color: '#bbf7d0' } 
          : p
      );

      return { actions: updatedActions, postIts: updatedPostIts };
    });

    return newId;
  },
  
  updateAction: (id, data) => {
    set((state) => {
      const updatedActions = state.actions.map((a) =>
        a.id === id ? { ...a, ...data, updatedAt: new Date().toISOString() } : a
      );
      
      // Sincronizar com o mapa (Cards e Post-its)
      const action = updatedActions.find(a => a.id === id);
      if (action) {
        const { sheets, activeSheetId, postIts } = state;
        
        // 1. Sincronizar com Post-its
        const updatedPostIts = postIts.map(p => {
          if (p.id === id || p.id === action.linkedNodeId || p.id === action.linkedPostItId) {
            const hasTitleChange = data.title !== undefined && p.text !== data.title;
            if (hasTitleChange) {
              return { ...p, text: data.title! };
            }
          }
          return p;
        });

        // 2. Sincronizar com Cards
        if (activeSheetId) {
          const updatedSheets = sheets.map(sheet => ({
            ...sheet,
            cards: sheet.cards.map(card => {
              if (card.id === id || card.id === action.linkedNodeId) {
                // Evitar loops: apenas atualizar se houver mudança real
                const hasTitleChange = data.title !== undefined && card.atividade !== data.title;
                const hasStatusChange = data.status !== undefined && card.actionStatus !== data.status;
                
                if (hasTitleChange || hasStatusChange) {
                  return {
                    ...card,
                    atividade: data.title !== undefined ? data.title : card.atividade,
                    actionStatus: data.status !== undefined ? data.status : card.actionStatus,
                    updatedAt: action.updatedAt
                  };
                }
              }
              return card;
            })
          }));
          return { actions: updatedActions, sheets: updatedSheets, postIts: updatedPostIts };
        }
        
        return { actions: updatedActions, postIts: updatedPostIts };
      }
      
      return { actions: updatedActions };
    });
  },
  
  deleteAction: (id) => {
    set((state) => {
      const actionToDelete = state.actions.find((a) => a.id === id);
      const updatedActions = state.actions.filter((a) => a.id !== id);
      
      // Sincronizar com o mapa: Deletar o card correspondente
      const { sheets } = state;
      const updatedSheets = sheets.map(sheet => ({
        ...sheet,
        cards: sheet.cards.filter(card => card.id !== id && card.id !== actionToDelete?.linkedNodeId)
      }));

      let updatedActionPlans = state.actionPlans;
      if (actionToDelete?.actionPlanId) {
        updatedActionPlans = state.actionPlans.map((ap) =>
          ap.id === actionToDelete.actionPlanId
            ? { ...ap, actionIds: ap.actionIds.filter((actionId) => actionId !== id), updatedAt: new Date().toISOString() }
            : ap
        );
      }
      return { actions: updatedActions, actionPlans: updatedActionPlans, sheets: updatedSheets };
    });
  },
  
  getAction: (id) => get().actions.find((a) => a.id === id),
  
  getActions: () => get().actions,
  
  setActions: (actions) => set({ actions }),
  
  // Mesclar ações de um projeto específico, mantendo ações de outros projetos
  mergeProjectActions: (projectId, actions) => {
    set((state) => {
      // Remover ações antigas deste projeto
      const otherProjectsActions = state.actions.filter(a => a.projectId !== projectId);
      // Adicionar ações novas deste projeto
      return { actions: [...otherProjectsActions, ...actions] };
    });
  },
  
  getActionsByPlan: (actionPlanId) => get().actions.filter((a) => a.actionPlanId === actionPlanId),
  
  getActionsByNode: (linkedNodeId) => get().actions.filter((a) => a.linkedNodeId === linkedNodeId),
  
  getActionsByProject: (projectId) => get().actions.filter((a) => a.projectId === projectId),

  moveAction: (id, newStatus) => {
    set((state) => {
      const updatedActions = state.actions.map((action) =>
        action.id === id
          ? { ...action, status: newStatus, updatedAt: new Date().toISOString() }
          : action
      );
      
      // Sincronizar com o mapa se houver card vinculado ou se o ID for o mesmo
      const action = updatedActions.find(a => a.id === id);
      if (action) {
        const { sheets, activeSheetId } = state;
        if (activeSheetId) {
          const updatedSheets = sheets.map(sheet => ({
            ...sheet,
            cards: sheet.cards.map(card => {
              if (card.id === action.linkedNodeId || card.id === action.id) {
                return { ...card, updatedAt: action.updatedAt };
              }
              return card;
            })
          }));
          return { actions: updatedActions, sheets: updatedSheets };
        }
      }
      
      return { actions: updatedActions };
    });
  },
  
  // ============================================================================
  // SIMULA!ÒO DE CENÁRIO FUTURO (KAIZEN)
  // ============================================================================
  simulateFutureState: () => {
    const state = get();
    const activeSheet = state.getActiveSheet();
    if (!activeSheet) {
      return {
        futureCards: [],
        futureMetrics: state.metrics,
        currentMetrics: state.metrics,
        improvements: {
          leadTimeReduction: 0,
          costReduction: 0,
          efficiencyGain: 0,
          annualSaving: 0,
        },
      };
    }

    const currentCards = activeSheet.cards;
    const currentMetrics = calculateMetrics(currentCards);
    
    // Clonar cards e aplicar melhorias Kaizen
    const futureCards = currentCards.map(card => {
      const clonedCard = { ...card };
      
      // Apenas processar atividades
      if (card.tipo !== 'atividade') return clonedCard;
      
      const tp = Number(card.lt || 0); // Lead Time (TP)
      const tra = Number(card.tt || 0); // Tempo de Realização (TRA)
      const waitTime = Math.max(0, tp - tra);
      
      // REGRA 1: Reduzir espera pela metade onde existe espera
      if (waitTime > 0) {
        const newWaitTime = waitTime * 0.5;
        clonedCard.lt = tra + newWaitTime;
      }
      
      // REGRA 2: Melhorar produtividade onde ocupação é crítica (> 90%)
      const occupation = card.carga || 0;
      if (occupation > 90 && tra > 0) {
        // Reduzir TRA em 10% (automação/melhoria de processo)
        clonedCard.tt = tra * 0.9;
        // Atualizar TP também se não havia espera
        if (waitTime === 0) {
          clonedCard.lt = clonedCard.tt;
        }
      }
      
      return clonedCard;
    });
    
    // Recalcular métricas do estado futuro
    const futureMetrics = calculateMetrics(futureCards);
    
    // Calcular melhorias
    const currentLeadTime = parseFloat(currentMetrics.longestPath) || 0;
    const futureLeadTime = parseFloat(futureMetrics.longestPath) || 0;
    const leadTimeReduction = ((currentLeadTime - futureLeadTime) / currentLeadTime) * 100;
    
    const currentCost = currentMetrics.monthlyOpCost || 0;
    const futureCost = futureMetrics.monthlyOpCost || 0;
    const costReduction = ((currentCost - futureCost) / currentCost) * 100;
    
    const currentEfficiency = parseFloat(currentMetrics.flowEfficiency) || 0;
    const futureEfficiency = parseFloat(futureMetrics.flowEfficiency) || 0;
    const efficiencyGain = futureEfficiency - currentEfficiency;
    
    // Calcular economia anual (R$/ano)
    const monthlySaving = currentCost - futureCost;
    const annualSaving = monthlySaving * 12;
    
    return {
      futureCards,
      futureMetrics,
      currentMetrics,
      improvements: {
        leadTimeReduction: isFinite(leadTimeReduction) ? leadTimeReduction : 0,
        costReduction: isFinite(costReduction) ? costReduction : 0,
        efficiencyGain: isFinite(efficiencyGain) ? efficiencyGain : 0,
        annualSaving: isFinite(annualSaving) ? annualSaving : 0,
      },
    };
  },
  
  getFutureMetrics: () => {
    const state = get();
    const activeSheet = state.getActiveSheet();
    if (!activeSheet) {
      return { current: state.metrics, future: state.metrics };
    }

    const currentCards = activeSheet.cards;
    const currentMetrics = calculateMetrics(currentCards);
    const postIts = state.postIts;
    
    // Filtrar post-its de ação que possuem impactos definidos
    const actionPostIts = postIts.filter(p => 
      p.impactos && p.impactos.length > 0
    );
    
    // Se não houver post-its com impactos, retornar métricas iguais
    if (actionPostIts.length === 0) {
      return { current: currentMetrics, future: currentMetrics };
    }

    // Clonar os cards para aplicar simulação
    const futureCards = currentCards.map(card => ({ ...card }));
    
    // Aplicar impactos de cada post-it sobre os cards clonados
    actionPostIts.forEach(postIt => {
      if (!postIt.impactos) return;
      
      postIt.impactos.forEach(impact => {
        const targetCard = futureCards.find(c => c.id === impact.targetNodeId);
        if (!targetCard) return;
        
        const reducaoFator = (100 - impact.reducao) / 100; // Ex: 20% redução = 0.8
        
        switch (impact.tipo) {
          case 'TRA':
            // Reduzir TRA (tempo de processamento em minutos)
            if (targetCard.tt) {
              targetCard.tt = targetCard.tt * reducaoFator;
            }
            break;
          case 'TP':
            // Reduzir TP (lead time em dias)
            if (targetCard.lt) {
              targetCard.lt = targetCard.lt * reducaoFator;
            }
            break;
          case 'ELIMINAR':
            // Marcar card como eliminado (zerar tempos)
            targetCard.tt = 0;
            targetCard.lt = 0;
            targetCard.status = 'removido';
            break;
        }
      });
    });
    
    // Recalcular métricas com os cards modificados
    const futureMetrics = calculateMetrics(futureCards);
    
    return {
      current: currentMetrics,
      future: futureMetrics
    };
  },
  
  // ==========================================================================
  // x} ISOLAMENTO DE DADOS ENTRE MODOS (current / kaizen / future)
  // Cada modo possui seu próprio snapshot de cards.
  // Regra: alterações no modo futuro NÒO afetam kaizen.
  //        alterações no modo kaizen ou futuro NÒO afetam o mapa atual.
  // ==========================================================================
  saveAndSwitchMode: (fromMode, toMode) => {
    const state = get();
    const activeSheet = state.getActiveSheet();
    if (!activeSheet || fromMode === toMode) return;

    // Deep clone de cards para snapshot (preserva conexões, resources, backups)
    const cloneCards = (cards: CardData[]): CardData[] =>
      cards.map(c => ({
        ...c,
        conexoes_saida: c.conexoes_saida.map(conn => ({ ...conn })),
        resources: c.resources ? [...c.resources] : [],
        originalConexoes: c.originalConexoes
          ? c.originalConexoes.map(conn => ({ ...conn }))
          : undefined,
        vsmSupermarketOverlayOffset: c.vsmSupermarketOverlayOffset
          ? { ...c.vsmSupermarketOverlayOffset }
          : undefined,
      }));

    // Deep clone de postIts para snapshot
    const clonePostIts = (pIts: PostIt[]): PostIt[] =>
      pIts.map(p => ({
        ...p,
        impactos: p.impactos ? p.impactos.map(i => ({ ...i })) : undefined,
        relativeOffset: p.relativeOffset ? { ...p.relativeOffset } : undefined,
      }));

    // 1. Salvar snapshot do modo que estamos SAINDO (cards + postIts)
    const snapshots = { ...state._modeSnapshots };
    snapshots[fromMode] = {
      cards: cloneCards(activeSheet.cards),
      postIts: clonePostIts(state.postIts),
    };

    // 2. Restaurar snapshot do modo DESTINO
    if (toMode === 'future') {
      const source = projectionInputs(snapshots);
      if (source.cards.length > 0) {
        const projectedCards = projectFutureOfficeCards(cloneCards(source.cards), source.postIts);
        const restoredPostIts = clonePostIts(source.postIts);
        snapshots.future = {
          cards: cloneCards(projectedCards),
          postIts: clonePostIts(source.postIts),
        };
        const updatedSheets = state.sheets.map(s =>
          s.id === state.activeSheetId ? { ...s, cards: projectedCards } : s
        );
        const metrics = calculateMetrics(projectedCards, state.mapType);
        set({
          sheets: updatedSheets,
          postIts: restoredPostIts,
          metrics,
          _modeSnapshots: snapshots,
          hasFutureMap: postItsHaveQuantifiedImpact(source.postIts) || state.hasFutureMap,
          futureMapSourceKey: futureInputKey(source.cards, source.postIts),
        });
      } else {
        const currentCards = state.sheets.find(s => s.id === state.activeSheetId)?.cards || [];
        const metrics = calculateMetrics(currentCards, state.mapType);
        set({ metrics, _modeSnapshots: snapshots });
      }
    } else {
      // current ou kaizen: restaurar snapshot se existir.
      // A primeira entrada em Otimização herda o mapa atual, porque o snapshot
      // inicial de kaizen ainda é o chassi em branco.
      let targetSnapshot = snapshots[toMode];
      let nextKaizenBaseline: string | null = null;
      if (toMode === 'kaizen' && snapshots.current) {
        const currentKey = futureCardsKey(snapshots.current.cards);
        if (currentKey !== state.kaizenBaselineKey) {
          const currentIds = new Set(snapshots.current.cards.map(card => card.id));
          const extras = (snapshots.kaizen?.cards ?? []).filter(card => !currentIds.has(card.id) && isOptimizationOnlyCard(card));
          snapshots.kaizen = {
            cards: cloneCards([...snapshots.current.cards, ...extras]),
            postIts: clonePostIts(snapshots.kaizen?.postIts ?? snapshots.current.postIts),
          };
          targetSnapshot = snapshots.kaizen;
          nextKaizenBaseline = currentKey;
        }
      }
      if (toMode === 'kaizen' && snapshots.kaizen && snapshots.current) {
        const kaizenTimes = snapshots.kaizen.cards.reduce((sum, card) => sum + Number(card.tt || 0) + Number(card.lt || 0), 0);
        const currentTimes = snapshots.current.cards.reduce((sum, card) => sum + Number(card.tt || 0) + Number(card.lt || 0), 0);
        const kaizenUntouched = snapshots.kaizen.postIts.length === 0 && !postItsHaveQuantifiedImpact(snapshots.kaizen.postIts);
        const currentIsAhead = snapshots.current.cards.length > snapshots.kaizen.cards.length || currentTimes > kaizenTimes;
        if (kaizenUntouched && currentIsAhead) {
          snapshots.kaizen = {
            cards: cloneCards(snapshots.current.cards),
            postIts: clonePostIts(snapshots.current.postIts),
          };
          targetSnapshot = snapshots.kaizen;
        }
      }
      if (targetSnapshot) {
        const restoredCards = cloneCards(targetSnapshot.cards);
        const restoredPostIts = clonePostIts(targetSnapshot.postIts);
        const updatedSheets = state.sheets.map(s =>
          s.id === state.activeSheetId ? { ...s, cards: restoredCards } : s
        );
        const metrics = calculateMetrics(restoredCards, state.mapType);
        set({
          sheets: updatedSheets,
          postIts: restoredPostIts,
          metrics,
          _modeSnapshots: snapshots,
          ...(nextKaizenBaseline ? { kaizenBaselineKey: nextKaizenBaseline } : {}),
        });
      } else {
        const currentCards = state.sheets.find(s => s.id === state.activeSheetId)?.cards || [];
        const metrics = calculateMetrics(currentCards, state.mapType);
        set({
          metrics,
          _modeSnapshots: snapshots,
          ...(nextKaizenBaseline ? { kaizenBaselineKey: nextKaizenBaseline } : {}),
        });
      }
    }

  },

  // Actions - Project Stages
  setStage: (stage: ProjectStage) => set((state) => {
    const stageOrder: ProjectStage[] = ['diagnosis', 'kaizen', 'planning', 'execution', 'sustainment'];
    const targetIndex = stageOrder.indexOf(stage);
    const hasSkippedPrevious = targetIndex > 0 && stageOrder
      .slice(0, targetIndex)
      .some(prevStage => !state.completedStages.includes(prevStage));

    return {
      stage,
      journeyDeviationAlert: state.journeyDeviationAlert || hasSkippedPrevious,
    };
  }),
  
  completeStage: (stage: ProjectStage) => set((state) => {
    const stageOrder: ProjectStage[] = ['diagnosis', 'kaizen', 'planning', 'execution', 'sustainment'];
    const nextCompletedStages = state.completedStages.includes(stage)
      ? state.completedStages
      : [...state.completedStages, stage];

    const currentStageIndex = stageOrder.indexOf(state.stage);
    const stillHasGap = currentStageIndex > 0 && stageOrder
      .slice(0, currentStageIndex)
      .some(prevStage => !nextCompletedStages.includes(prevStage));

    if (!state.completedStages.includes(stage) || state.journeyDeviationAlert !== stillHasGap) {
      return {
        completedStages: nextCompletedStages,
        journeyDeviationAlert: stillHasGap,
      };
    }
    return state;
  }),

  clearJourneyDeviationAlert: () => set({ journeyDeviationAlert: false }),

  resetNewProjectState: () => set({
    currentMapDisplayName: null,
    sheets: [],
    activeSheetId: null,
    stage: 'diagnosis',
    completedStages: [],
    dashboardMetrics: { totalCost: 0, totalFTE: 0, bottleneckId: null, bottleneckName: null, leadTime: 0, efficiency: 0 },
    viewMode: 'current_clean',
    isKaizenMode: false,
    hasFutureMap: false,
    futureMapSourceKey: null,
    kaizenBaselineKey: null,
    // x} CRITICAL: zera os três snapshots simultaneamente para evitar
    // que dados de cenários anteriores reapareçam ao trocar de aba.
    _modeSnapshots: { current: null, kaizen: null, future: null },
    postIts: [],
    actions: [],
    selectedCardId: null,
    simulatedDemand: null,
    simulatedHeadcount: null,
    simulationBaselineData: null,
    optimizationSession: null,
    optimizationLayer: [],
    journeyDeviationAlert: false,
  }),

  initializeBlankProject: ({ mapType, baseCards, displayName = null }) => {
    // Deep clone determinístico — o mesmo chassi precisa estar em current/kaizen/future
    // como instâncias INDEPENDENTES (mutações em uma aba não devem vazar para outra).
    const cloneCards = (cards: CardData[]): CardData[] =>
      cards.map(c => ({
        ...c,
        conexoes_saida: c.conexoes_saida.map(conn => ({ ...conn })),
        resources: c.resources ? [...c.resources] : [],
        originalConexoes: c.originalConexoes
          ? c.originalConexoes.map(conn => ({ ...conn }))
          : undefined,
        vsmSupermarketOverlayOffset: c.vsmSupermarketOverlayOffset
          ? { ...c.vsmSupermarketOverlayOffset }
          : undefined,
      }));

    const sheetId = generateId();
    const newSheet: Sheet = {
      id: sheetId,
      name: 'MAPA 1',
      cards: cloneCards(baseCards),
      viewport: { x: 0, y: 0, zoom: 1 },
    };

    const blankSnapshot = () => ({
      cards: cloneCards(baseCards),
      postIts: [] as PostIt[],
    });

    try { localStorage.setItem('wf_current_map_type', mapType); } catch { /* noop */ }
    try { localStorage.removeItem('wf_current_project_id'); } catch { /* noop */ }

    set({
      mapType,
      isNewMapDraft: true,
      currentProjectId: null,
      sheets: [newSheet],
      activeSheetId: sheetId,
      currentMapDisplayName: displayName,
      // Zera completamente camadas auxiliares
      postIts: [],
      actions: [],
      selectedCardId: null,
      simulatedDemand: null,
      simulatedHeadcount: null,
      simulationBaselineData: null,
      optimizationSession: null,
      optimizationLayer: [],
      // Reseta jornada/etapas (booleanos da Sidebar e fluxo de stages)
      stage: 'diagnosis',
      completedStages: [],
      journeyDeviationAlert: false,
      viewMode: 'current_clean',
      isKaizenMode: false,
    hasFutureMap: false,
    futureMapSourceKey: null,
    kaizenBaselineKey: null,
    // x} TRIPLO RESET: os três cenários recebem o MESMO chassi em branco,
      // clonado de forma independente. Garante que ao clicar em ATUAL,
      // OTIMIZAÇÃO ou FUTURO o canvas mostre apenas o chassi standard.
      _modeSnapshots: {
        current: blankSnapshot(),
        kaizen: blankSnapshot(),
        future: blankSnapshot(),
      },
      metrics: calculateMetrics(cloneCards(baseCards)),
    });
  },

  setViewMode: (mode: 'current_clean' | 'kaizen_work' | 'future_simulated') => {
    const state = get();
    const modeKey = (value: typeof mode): 'current' | 'kaizen' | 'future' => {
      if (value === 'kaizen_work') return 'kaizen';
      if (value === 'future_simulated') return 'future';
      return 'current';
    };

    if (state.viewMode !== mode) {
      state.saveAndSwitchMode(modeKey(state.viewMode), modeKey(mode));
    }

    if (mode === 'kaizen_work') {
      get().ensureOptimizationSession();
    }
    set({ viewMode: mode });
  },

  futureProjectionKey: () => {
    const snapshots = readWorkingSnapshots(get());
    const source = projectionInputs(snapshots);
    return futureInputKey(source.cards, source.postIts);
  },

  refreshFutureStateMap: () => {
    const state = get();
    const activeSheet = state.getActiveSheet();
    if (!activeSheet) return false;

    const snapshots = readWorkingSnapshots(state);
    const source = projectionInputs(snapshots);
    if (!source.cards.length || !postItsHaveQuantifiedImpact(source.postIts)) return false;

    const projected = projectFutureOfficeCards(cloneSnapshotCards(source.cards), cloneSnapshotPostIts(source.postIts));
    const modeKey = state.viewMode === 'kaizen_work'
      ? 'kaizen'
      : state.viewMode === 'future_simulated'
        ? 'future'
        : 'current';
    const nextSnapshots = { ...state._modeSnapshots };
    if (modeKey !== 'future' && snapshots[modeKey]) {
      nextSnapshots[modeKey] = {
        cards: cloneSnapshotCards(snapshots[modeKey].cards),
        postIts: cloneSnapshotPostIts(snapshots[modeKey].postIts),
      };
    }
    nextSnapshots.future = {
      cards: cloneSnapshotCards(projected),
      postIts: cloneSnapshotPostIts(source.postIts),
    };

    const patch: Partial<ReturnType<typeof get>> = {
      _modeSnapshots: nextSnapshots,
      hasFutureMap: true,
      futureMapSourceKey: futureInputKey(source.cards, source.postIts),
      isKaizenMode: false,
    };
    if (state.viewMode === 'future_simulated') {
      patch.sheets = state.sheets.map(sheet =>
        sheet.id === state.activeSheetId ? { ...sheet, cards: projected } : sheet
      );
      patch.postIts = cloneSnapshotPostIts(source.postIts);
      patch.metrics = calculateMetrics(projected);
    }
    set(patch);
    return true;
  },

  generateFutureStateMap: () => {
    const refreshed = get().refreshFutureStateMap();
    if (!refreshed) return false;
    if (get().viewMode !== 'future_simulated') {
      get().setViewMode('future_simulated');
    }
    set({ hasFutureMap: true, isKaizenMode: false });
    return true;
  },
  
  restoreOriginalState: () => {
    const state = get();
    const activeSheet = state.getActiveSheet();
    
    if (!activeSheet) {
      return;
    }
    
    // Restaurar valores originais dos cards (incluindo conexões)
    const restoredCards = activeSheet.cards.map(card => {
      if (!card.futureApplied) return card;
      
      return {
        ...card,
        tt: card.originalTT !== undefined ? card.originalTT : card.tt,
        lt: card.originalLT !== undefined ? card.originalLT : card.lt,
        status: card.originalStatus !== undefined ? card.originalStatus : (card.status === 'removido' ? 'ativo' : card.status),
        conexoes_saida: card.originalConexoes !== undefined ? [...card.originalConexoes] : card.conexoes_saida,
        futureApplied: false
      };
    });
    
    // Atualizar cards na sheet ativa
    const updatedSheets = state.sheets.map(sheet =>
      sheet.id === state.activeSheetId
        ? { ...sheet, cards: restoredCards }
        : sheet
    );
    
    // Recalcular métricas
    const newMetrics = calculateMetrics(restoredCards);
    
    set({ 
      sheets: updatedSheets,
      metrics: newMetrics,
      viewMode: 'current_clean'
    });
    
  },
}));

